import React from "react";
import WebProfitSpinner from "../../components/system/WebProfitSpinner";
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import UploadRoundedIcon from "@mui/icons-material/UploadRounded";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import DesignServicesRoundedIcon from "@mui/icons-material/DesignServicesRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import QrCodeScannerRoundedIcon from "@mui/icons-material/QrCodeScannerRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import LockClockRoundedIcon from "@mui/icons-material/LockClockRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import RestaurantMenuRoundedIcon from "@mui/icons-material/RestaurantMenuRounded";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { productsApi } from "../../api/products";
import { promotionsApi } from "../../api/promotions";
import { recipesApi } from "../../api/recipes";
import { projectsApi } from "../../api/projects";
import { useProjects } from "../../context/ProjectsContext";
import { isProjectModuleEnabled } from "../../utils/projectModules";
import { isProjectCapabilityEnabled } from "../../utils/projectCapabilities";
import ModuleDisabledState from "../../components/dashboard/ModuleDisabledState";
import EditorialIntro from "../../components/dashboard/EditorialIntro";
import ResponsiveListControls from "../../components/dashboard/ResponsiveListControls";
import ResponsiveOperationalTable from "../../components/dashboard/ResponsiveOperationalTable";
import DashboardLoadingState from "../../components/dashboard/DashboardLoadingState";
import { getProductImageUrls } from "../../utils/productMedia";
import {
  formatImageSize,
  optimizeProductImages,
} from "../../utils/productImageUpload";
import {
  PRODUCT_IMAGE_MAX_SOURCE_BYTES,
  describeImageUploadError,
  uploadProgressPercent,
} from "../../utils/imageUpload";
import ItemOperationalFields, { capabilitiesForKind } from "../../components/dashboard/items/ItemOperationalFields";
import ProductVariantsEditor from "../../components/dashboard/items/ProductVariantsEditor";
import BarcodeScannerDialog from "../../components/dashboard/items/BarcodeScannerDialog";
import ProductImportWizard from "../../components/dashboard/items/import/ProductImportWizard";
import GastronomicItemWizard from "../../components/dashboard/items/GastronomicItemWizard";
import GastronomicMenuOrderDialog from "../../components/dashboard/items/GastronomicMenuOrderDialog";
import ItemImagesEditor from "../../components/dashboard/items/ItemImagesEditor";
import GastronomicAdvancedEditor from "../../components/dashboard/items/GastronomicAdvancedEditor";
import GastronomicPromotionsEditor, { CURRENT_PRODUCT } from "../../components/dashboard/items/GastronomicPromotionsEditor";
import GastronomicSectionPositionEditor from "../../components/dashboard/items/GastronomicSectionPositionEditor";
import RecipesWorkspace from "../../components/dashboard/gastronomy/RecipesWorkspace";
import QuantityDiscountsEditor, {
  normalizeQuantityDiscountRows,
  quantityDiscountEligibility,
  validateQuantityDiscountRows,
} from "../../components/dashboard/items/QuantityDiscountsEditor";
import { hasProjectPermission, normalizeProjectAccess } from "../../utils/projectAccess";
import {
  isTrialProject,
  TRIAL_ITEMS_LIMIT,
  TRIAL_PRODUCTS_EVENT,
} from "../../utils/trialOnboarding";
import { isValidCalendlyUrl } from "../../utils/calendly";
import { downloadBarcodeLabelsPdf } from "../../utils/barcodeLabelsPdf";

const BRAND = { orange: "#F35A3A", dark: "var(--wp-panel-text-primary, #19242F)" };
const MAX_IMAGES = 8;

const REMOVE_IMAGE_BUTTON_SX = {
  position: "absolute",
  top: -8,
  right: -8,
  width: 44,
  height: 44,
  p: 0,
  bgcolor: "transparent",
  zIndex: 1,
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    right: 0,
    width: 24,
    height: 24,
    borderRadius: "50%",
    bgcolor: "var(--wp-panel-surface-elevated, white)",
    boxShadow: 1,
  },
  "&:hover": { bgcolor: "transparent" },
  "&:hover::before": { bgcolor: "var(--wp-panel-surface-soft, #F7F8FB)" },
  "&.Mui-focusVisible": {
    outline: `2px solid ${BRAND.orange}`,
    outlineOffset: 1,
  },
};

const emptyForm = {
  itemType: "product",
  erpItemKind: "product",
  name: "",
  description: "",
  category: "General",
  subcategory: "",
  gastronomicEnabled: false,
  gastronomicAvailable: true,
  gastronomicShortDescription: "",
  gastronomicDietaryNotes: "",
  gastronomicSection: "",
  gastronomicSectionOrder: "0",
  gastronomicSortOrder: "0",
  gastronomicFeatured: false,
  code: "",
  unit: "",
  priceType: "fixed",
  price: "0",
  quantityPriceTiers: [],
  stock: "0",
  durationMinutes: "",
  modality: "",
  requiresBooking: false,
  requiresShipping: true,
  bookingProvider: "none",
  calendlyUrl: "",
  calendlyDisplayMode: "popup",
  bookingConfirmationMode: "manual",
  bookingPaymentMode: "none",
  bookingDepositType: "fixed",
  bookingDepositValue: "0",
  capacity: "1",
  bufferBeforeMinutes: "0",
  bufferAfterMinutes: "0",
  serviceLocation: "",
  meetingUrl: "",
  bookingInstructions: "",
  cancellationWindowHours: "0",
  tags: "",
  attributes: [],
  hasVariants: false,
  variantAttributes: [],
  variants: [],
  active: true,
  membershipEligible: false,
  membershipPeriodValue: "1",
  membershipPeriodUnit: "months",
  membershipReferencePrice: "",
  membershipUsageEnabled: false,
  membershipIncludedUses: "",
  membershipUsageLabel: "usos",
  ...capabilitiesForKind("product"),
  lastCost: "0",
  averageCost: "0",
  standardCost: "0",
  costingMethod: "last_cost",
  costCurrency: "ARS",
  allowNegativeStock: false,
  minimumStock: "0",
  reorderPoint: "0",
  inventoryLocation: "",
  salesAccountCode: "SALES",
  purchaseAccountCode: "PURCHASES",
  inventoryAccountCode: "INVENTORY",
  costAccountCode: "COST_OF_GOODS",
  expenseAccountCode: "OTHER_EXPENSE",
  taxProfileCode: "",
  taxRate: "0",
  taxIncludedInPrice: false,
};

function normalizeItemType(product = {}) {
  const value = String(
    product.itemType || product.type || product.tipo || "product",
  )
    .trim()
    .toLowerCase();
  return ["service", "servicio"].includes(value) ? "service" : "product";
}

const ITEM_KIND_LABELS = {
  product: "Producto para vender",
  service: "Servicio para vender",
  supply: "Insumo o consumible",
  expense: "Gasto habitual",
  asset: "Activo o equipamiento",
  charge: "Cargo o concepto",
};

function normalizeItemKind(product = {}) {
  const value = String(
    product.erpItemKind || product.itemKind || product.itemClass || normalizeItemType(product),
  )
    .trim()
    .toLowerCase();
  return ITEM_KIND_LABELS[value] ? value : normalizeItemType(product);
}

function productCapabilities(product = {}) {
  const itemKind = normalizeItemKind(product);
  const defaults = capabilitiesForKind(itemKind);
  const supplied = {
    ...(product.capabilities || {}),
    ...(product.operational || {}),
  };

  return {
    ...defaults,
    ...supplied,
    reservable: Boolean(
      supplied.reservable ??
        product.requiresBooking ??
        product.requiereReserva ??
        defaults.reservable,
    ),
    shippable: Boolean(
      supplied.shippable ??
        product.requiresShipping ??
        product.requiereEnvio ??
        defaults.shippable,
    ),
  };
}

function supportsInventoryForKind(kind) {
  return kind === "product" || kind === "supply";
}

function supportsVariantsForKind(kind) {
  return supportsInventoryForKind(kind);
}

function isReservableService(form = {}) {
  return (
    form.erpItemKind === "service" &&
    Boolean(form.reservable || form.requiresBooking)
  );
}

function normalizePriceType(product = {}) {
  const value = String(product.priceType || product.precioTipo || "fixed")
    .trim()
    .toLowerCase();
  if (["from", "desde"].includes(value)) return "from";
  if (["quote", "consultar"].includes(value)) return "quote";
  return "fixed";
}

function attributesToRows(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return [];
  return Object.entries(value)
    .map(([label, detail]) => ({
      label: String(label),
      value: String(detail ?? ""),
    }))
    .filter((row) => row.label || row.value);
}

function rowsToAttributes(rows = []) {
  return rows.reduce((result, row) => {
    const label = String(row?.label || "").trim();
    const value = String(row?.value || "").trim();
    if (label && value) result[label] = value;
    return result;
  }, {});
}

function tagsToText(product = {}) {
  const tags = product.tags || product.etiquetas || [];
  return (Array.isArray(tags) ? tags : [])
    .map((tag) => (typeof tag === "string" ? tag : tag?.title))
    .filter(Boolean)
    .join(", ");
}

function variantAttributesToForm(product = {}) {
  const source = product.variantAttributes || product.variantOptions || [];
  return (Array.isArray(source) ? source : []).map((axis, index) => ({
    key: String(axis?.key || axis?.id || `opcion_${index + 1}`),
    label: String(axis?.label || axis?.name || `Opción ${index + 1}`),
    values: Array.isArray(axis?.values || axis?.options)
      ? (axis.values || axis.options).map(String).filter(Boolean)
      : [],
    displayType: axis?.displayType || axis?.type || "button",
  }));
}

function variantsToForm(product = {}) {
  const source = product.variants || product.variations || [];
  return (Array.isArray(source) ? source : []).map((variant) => ({
    id: String(variant?.id || variant?._id || ""),
    attributes: variant?.attributes || variant?.combination || {},
    sku: variant?.sku || variant?.codigo || "",
    barcode: variant?.barcode || "",
    price:
      variant?.price === null || variant?.price === undefined
        ? ""
        : String(variant.price),
    stock: String(variant?.stock ?? 0),
    imageRef: variant?.imagePath || "",
    active: variant?.active !== false,
  }));
}

function quantityPriceTiersToForm(product = {}) {
  const source =
    product.quantityPriceTiers ||
    product.quantityDiscounts ||
    product.descuentosPorCantidad ||
    [];
  return (Array.isArray(source) ? source : []).map((tier, index) => ({
    key: `quantity-tier-${productId(product) || "item"}-${index}`,
    minQuantity: String(tier?.minQuantity ?? tier?.quantity ?? ""),
    unitPrice: String(tier?.unitPrice ?? tier?.price ?? ""),
  }));
}

function validateVariantForm(form = {}) {
  if (!form.hasVariants || !supportsVariantsForKind(form.erpItemKind)) return "";
  const axes = Array.isArray(form.variantAttributes) ? form.variantAttributes : [];
  const variants = Array.isArray(form.variants) ? form.variants : [];
  if (!axes.length) return "Agregá al menos una característica, por ejemplo Color o Talle.";
  for (const axis of axes) {
    if (!String(axis.label || "").trim()) return "Completá el nombre de cada característica.";
    if (!Array.isArray(axis.values) || !axis.values.length) {
      return `Agregá opciones para ${axis.label || "cada característica"}.`;
    }
  }
  if (!variants.length) return "Generá las combinaciones antes de guardar el producto.";
  if (variants.length > 250) return "El producto no puede superar 250 variantes.";

  const skuSet = new Set();
  const combinationSet = new Set();
  for (const variant of variants) {
    const combinationParts = [];
    for (const axis of axes) {
      const value = String(variant.attributes?.[axis.key] || "").trim();
      if (!value) return `Falta ${axis.label || "una opción"} en una variante.`;
      combinationParts.push(`${axis.key}:${value.toLocaleLowerCase("es")}`);
    }
    const combination = combinationParts.join("|");
    if (combinationSet.has(combination)) return "Hay combinaciones de variantes repetidas.";
    combinationSet.add(combination);

    const sku = String(variant.sku || "").trim().toLocaleLowerCase("es");
    if (sku && skuSet.has(sku)) return `El SKU ${variant.sku} está repetido.`;
    if (sku) skuSet.add(sku);
  }
  return "";
}

function variantImageOptions(existingImages = [], previewUrls = []) {
  return [
    ...existingImages.map((image) => ({ ref: image.identifier, url: image.url })),
    ...previewUrls.map((url) => ({ ref: url, url })),
  ];
}

function normalizeProductPayload(
  form,
  imageOptions = [],
  { membershipsEnabled = false } = {},
) {
  const isService = form.erpItemKind === "service";
  const reservable = isReservableService(form);
  const shippable =
    form.erpItemKind === "product" &&
    Boolean(form.shippable ?? form.requiresShipping);
  const hasVariants =
    supportsVariantsForKind(form.erpItemKind) && Boolean(form.hasVariants);
  const variants = hasVariants
    ? (Array.isArray(form.variants) ? form.variants : []).map((variant) => ({
        ...(variant.id ? { id: variant.id } : {}),
        attributes: variant.attributes || {},
        sku: String(variant.sku || "").trim(),
        barcode: String(variant.barcode || "").trim(),
        price:
          variant.price === "" || variant.price === null || variant.price === undefined
            ? null
            : Number(variant.price),
        imageIndex: imageOptions.findIndex((option) => option.ref === variant.imageRef),
        active: variant.active !== false,
      }))
    : [];
  const quantityPricingAllowed = quantityDiscountEligibility(form).allowed;
  return {
    itemType: isService ? "service" : "product",
    erpItemKind: form.erpItemKind,
    itemKind: form.erpItemKind,
    name: form.name,
    description: form.description,
    category: form.category || "General",
    subcategory: form.subcategory || "",
    code: form.code,
    unit: form.unit,
    priceType: form.priceType,
    price: Number(form.price || 0),
    quantityPriceTiers: quantityPricingAllowed
      ? normalizeQuantityDiscountRows(form.quantityPriceTiers)
      : [],
    hasVariants,
    variantAttributes: hasVariants
      ? (form.variantAttributes || []).map((axis) => ({
          key: axis.key,
          label: axis.label,
          values: Array.isArray(axis.values) ? axis.values : [],
          displayType: axis.displayType || "button",
        }))
      : [],
    variants,
    operational: {
      sellable: Boolean(form.sellable),
      purchasable: Boolean(form.purchasable),
      reservable,
      shippable,
      publicVisible: Boolean(form.publicVisible),
      expenseEnabled: Boolean(form.expenseEnabled),
      internalConsumptionEnabled: Boolean(form.internalConsumptionEnabled),
      stockTracked: Boolean(form.stockTracked),
      ...(membershipsEnabled ? { membershipEligible: Boolean(form.membershipEligible) } : {}),
    },
    inventory: {
      allowNegativeStock: Boolean(form.allowNegativeStock),
      minimumStock: Number(form.minimumStock || 0),
      reorderPoint: Number(form.reorderPoint || 0),
      location: String(form.inventoryLocation || "").trim(),
    },
    ...(membershipsEnabled
      ? {
          membershipPlan: form.membershipEligible
            ? {
                periodValue: Number(form.membershipPeriodValue || 1),
                periodUnit: form.membershipPeriodUnit || "months",
                referencePrice: form.membershipReferencePrice === "" ? Number(form.price || 0) : Number(form.membershipReferencePrice || 0),
                usageEnabled: Boolean(form.membershipUsageEnabled),
                includedUses: form.membershipUsageEnabled ? Number(form.membershipIncludedUses || 0) : null,
                usageLabel: String(form.membershipUsageLabel || "usos").trim() || "usos",
              }
            : {
                usageEnabled: false,
                includedUses: null,
                usageLabel: String(form.membershipUsageLabel || "usos").trim() || "usos",
              },
        }
      : {}),
    costing: {
      lastCost: Number(form.lastCost || 0),
      averageCost: Number(form.averageCost || 0),
      standardCost: Number(form.standardCost || 0),
      costingMethod: form.costingMethod || "last_cost",
      currency: form.costCurrency || "ARS",
    },
    accounting: {
      salesAccountCode: form.salesAccountCode || "SALES",
      purchaseAccountCode: form.purchaseAccountCode || "PURCHASES",
      inventoryAccountCode: form.inventoryAccountCode || "INVENTORY",
      costAccountCode: form.costAccountCode || "COST_OF_GOODS",
      expenseAccountCode: form.expenseAccountCode || "OTHER_EXPENSE",
    },
    tax: {
      profileCode: form.taxProfileCode || "",
      rate: Number(form.taxRate || 0),
      includedInPrice: Boolean(form.taxIncludedInPrice),
    },
    durationMinutes: isService ? Number(form.durationMinutes || 0) : 0,
    modality: isService ? form.modality : "",
    requiresBooking: reservable,
    requiresShipping: shippable,
    bookingProvider: reservable
      ? (form.bookingProvider === "calendly" ? "calendly" : "webprofit")
      : "none",
    calendlyUrl:
      reservable && form.bookingProvider === "calendly"
        ? String(form.calendlyUrl || "").trim()
        : "",
    calendlyDisplayMode:
      reservable && form.bookingProvider === "calendly" && form.calendlyDisplayMode === "inline"
        ? "inline"
        : "popup",
    bookingConfirmationMode:
      reservable && form.bookingProvider !== "calendly" ? form.bookingConfirmationMode : "manual",
    bookingPaymentMode:
      reservable && form.bookingProvider !== "calendly" ? form.bookingPaymentMode : "none",
    bookingDepositType:
      reservable && form.bookingProvider !== "calendly" ? form.bookingDepositType : "fixed",
    bookingDepositValue:
      reservable && form.bookingProvider !== "calendly" ? Number(form.bookingDepositValue || 0) : 0,
    capacity:
      reservable && form.bookingProvider !== "calendly" ? Math.max(1, Number(form.capacity || 1)) : 1,
    bufferBeforeMinutes:
      reservable && form.bookingProvider !== "calendly" ? Number(form.bufferBeforeMinutes || 0) : 0,
    bufferAfterMinutes:
      reservable && form.bookingProvider !== "calendly" ? Number(form.bufferAfterMinutes || 0) : 0,
    serviceLocation: isService ? form.serviceLocation : "",
    meetingUrl:
      reservable && form.bookingProvider !== "calendly" ? form.meetingUrl : "",
    bookingInstructions: reservable ? form.bookingInstructions : "",
    cancellationWindowHours:
      reservable && form.bookingProvider !== "calendly" ? Number(form.cancellationWindowHours || 0) : 0,
    tags: String(form.tags || "")
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean),
    attributes: rowsToAttributes(form.attributes),
    active: Boolean(form.active),
  };
}

function promotionToDraft(promotion = {}) {
  return {
    key: promotion.id || `promo-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    id: promotion.id || "",
    name: promotion.name || "",
    active: promotion.active !== false,
    type: promotion.type === "multi_product_combo" ? "multi_product_combo" : "same_product_pack",
    fixedTotal: String(promotion.fixedTotal ?? ""),
    revision: promotion.revision || 1,
    reviewRequired: promotion.reviewRequired === true,
    reviewReasons: promotion.reviewReasons || [],
    lines: (promotion.lines || []).map((line) => ({
      productId: String(line.productId || ""),
      quantity: String(line.quantity || 1),
      variantMode: line.variantMode === "specific" ? "specific" : "any",
      variantIds: (line.variantIds || []).map(String),
    })),
  };
}

function validatePromotionDrafts(drafts = [], currentProductId = "") {
  for (const draft of drafts.filter((item) => !item._delete)) {
    if (!String(draft.name || "").trim()) return "Cada promoción necesita un nombre.";
    if (!(Number(draft.fixedTotal) > 0)) return "Cada promoción necesita un precio final mayor que cero.";
    const lines = Array.isArray(draft.lines) ? draft.lines : [];
    if (draft.type === "same_product_pack" && (lines.length !== 1 || Number(lines[0]?.quantity) < 2)) return "Un pack necesita al menos 2 unidades del mismo producto.";
    if (draft.type === "multi_product_combo" && lines.length < 2) return "Un combo necesita al menos dos productos o líneas.";
    for (const line of lines) {
      const resolvedId = line.productId === CURRENT_PRODUCT ? currentProductId || CURRENT_PRODUCT : line.productId;
      if (!resolvedId) return "Elegí todos los productos que forman la promoción.";
      const quantity = Number(line.quantity);
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > 99) return "Las cantidades de una promoción deben ser números enteros entre 1 y 99.";
      if (line.variantMode === "specific" && !(line.variantIds || []).length) return "Elegí las variantes habilitadas o usá ‘Cualquier opción’.";
    }
  }
  return "";
}

function promotionPayloadFromDraft(draft, currentProductId) {
  return {
    name: String(draft.name || "").trim(),
    active: draft.active !== false,
    type: draft.type === "multi_product_combo" ? "multi_product_combo" : "same_product_pack",
    fixedTotal: Number(draft.fixedTotal),
    lines: (draft.lines || []).map((line) => ({
      productId: line.productId === CURRENT_PRODUCT ? currentProductId : line.productId,
      quantity: Number(line.quantity),
      variantMode: line.variantMode === "specific" ? "specific" : "any",
      variantIds: line.variantMode === "specific" ? (line.variantIds || []) : [],
    })),
  };
}

function priceLabel(product = {}) {
  const price = Number(product.price ?? product.precio ?? 0);
  const type = normalizePriceType(product);
  if (type === "quote") return "A consultar";
  const formatted = `$${price.toLocaleString("es-AR")}`;
  return type === "from" ? `Desde ${formatted}` : formatted;
}

function durationLabel(minutes) {
  const value = Number(minutes || 0);
  if (!value) return "Duración no indicada";
  if (value < 60) return `${value} min`;
  const hours = Math.floor(value / 60);
  const rest = value % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

function productId(product) {
  const source = product || {};
  return (
    source.id ||
    source._id ||
    source.code ||
    source.codigo ||
    source.name ||
    source.nombre ||
    ""
  );
}

function productName(product = {}) {
  return product.name || product.nombre || "Sin nombre";
}

function productDescription(product = {}) {
  return product.description || product.descripcion || "";
}

function productCategory(product = {}) {
  return product.category || product.categoria || "General";
}

function productSubcategory(product = {}) {
  return product.subcategory || product.subcategoria || "";
}

function productCode(product = {}) {
  return product.code || product.codigo || "";
}

function productIsActive(product = {}) {
  return Boolean(product.active ?? product.activo ?? true);
}

function productStock(product = {}) {
  return Number(product.stock ?? 0);
}

function normalizeSearchValue(value) {
  return String(value ?? "")
    .trim()
    .toLocaleLowerCase("es");
}

function uniqueClassificationValues(values = []) {
  const byKey = new Map();
  (Array.isArray(values) ? values : []).forEach((value) => {
    const label = String(value || "").trim();
    const key = normalizeSearchValue(label);
    if (key && !byKey.has(key)) byKey.set(key, label);
  });
  return [...byKey.values()].sort((a, b) =>
    a.localeCompare(b, "es", { sensitivity: "base" }),
  );
}

function sameClassification(left, right) {
  const leftKey = normalizeSearchValue(left);
  const rightKey = normalizeSearchValue(right);
  return Boolean(leftKey && rightKey && leftKey === rightKey);
}

function validateBasicItemFields(form = {}) {
  const fields = {};
  const name = String(form.name || "").trim();
  const category = String(form.category || "").trim();
  const price = Number(form.price);

  if (!name) fields.name = "Ingresá un nombre para el ítem.";
  if (!category) fields.category = "Elegí o escribí una categoría.";
  if (form.price !== "" && (!Number.isFinite(price) || price < 0)) {
    fields.price = "Ingresá un precio válido, igual o mayor a 0.";
  }
  if (isReservableService(form) && Number(form.durationMinutes || 0) <= 0) {
    fields.durationMinutes = "Indicá cuánto dura el servicio para poder ofrecer turnos.";
  }
  if (isReservableService(form) && form.bookingProvider !== "calendly") {
    const capacityRaw = String(form.capacity ?? "").trim();
    const capacity = Number(capacityRaw);
    if (!capacityRaw || !Number.isFinite(capacity) || capacity < 1 || capacity > 10000) {
      fields.capacity = "Ingresá un cupo entre 1 y 10.000 por horario.";
    }
  }
  if (isReservableService(form) && form.bookingProvider === "calendly") {
    const calendlyUrl = String(form.calendlyUrl || "").trim();
    if (!calendlyUrl) {
      fields.calendlyUrl = "Pegá el enlace de Calendly que usarán tus clientes.";
    } else if (!isValidCalendlyUrl(calendlyUrl)) {
      fields.calendlyUrl = "Usá el enlace HTTPS de un evento real de Calendly, por ejemplo https://calendly.com/tu-negocio/turno.";
    }
  }

  return fields;
}

function validationFeedback(message, fields = {}) {
  return {
    kind: "validation",
    title: "Revisá los datos del ítem",
    message: String(message || "Hay datos que faltan o necesitan corrección."),
    fields,
  };
}

function saveErrorFeedback(error, { hasImages = false } = {}) {
  const status = Number(error?.status || 0);
  const code = String(error?.code || "").trim();
  const requestId = String(error?.requestId || "").trim();
  const message = hasImages
    ? describeImageUploadError(error, {
        action: "subir las imágenes del ítem",
        maxBytes: PRODUCT_IMAGE_MAX_SOURCE_BYTES,
      })
    : String(error?.message || "").trim();

  if ([400, 409, 413, 415, 422].includes(status)) {
    return {
      kind: "validation",
      title: "Hay algo para corregir",
      message: message || "Revisá los datos del ítem e intentá nuevamente.",
      fields: {},
    };
  }

  if ([401, 402, 403].includes(status)) {
    return {
      kind: "access",
      title: "No se puede guardar con este acceso",
      message: message || "Tu usuario o tu plan no permiten completar esta operación.",
      fields: {},
    };
  }

  return {
    kind: "system",
    title: "Error del sistema",
    message:
      status >= 500 || code === "INTERNAL_ERROR"
        ? "Los datos que cargaste no parecen ser el problema. El servidor no pudo guardar el ítem por una falla interna. Probá nuevamente; si se repite, usá el identificador de la solicitud para reportarlo."
        : "No pudimos comunicarnos correctamente con el servidor. Tus datos siguen en el formulario para que puedas volver a intentar.",
    detail: message && !/internal server error/i.test(message) ? message : "",
    requestId,
    code,
    fields: {},
  };
}

function reuseClassificationValue(value, options = []) {
  const normalized = String(value || "").trim();
  if (!normalized) return "";
  return (options || []).find((option) =>
    sameClassification(option, normalized),
  ) || normalized;
}

function ItemFormHeading({ step, title, description }) {
  return (
    <Stack direction="row" spacing={1.2} alignItems="flex-start">
      <Box
        sx={{
          width: 32,
          height: 32,
          flex: "0 0 auto",
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          bgcolor: BRAND.dark,
          color: "#fff",
          fontWeight: 950,
          fontSize: 13,
          boxShadow: "0 7px 18px rgba(var(--wp-panel-shadow-rgb, 25,36,47),.18)",
        }}
      >
        {step}
      </Box>
      <Box>
        <Typography sx={{ fontWeight: 950, color: BRAND.dark, lineHeight: 1.2 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", mt: 0.35, lineHeight: 1.45 }}>
          {description}
        </Typography>
      </Box>
    </Stack>
  );
}


export default function ProjectProductsPage() {
  const { projectId } = useParams();
  const mobile = useMediaQuery("(max-width:599.95px)", { noSsr: true });
  const compactLayout = useMediaQuery("(max-width:899.95px)", { noSsr: true });
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { projects, activeProject, setActiveProject, loadProjects } =
    useProjects();
  const [products, setProducts] = React.useState([]);
  const [recipeSummaries, setRecipeSummaries] = React.useState([]);
  const [recipesLoading, setRecipesLoading] = React.useState(false);
  const [trialCapacity, setTrialCapacity] = React.useState(null);
  const [trialCapacityResolved, setTrialCapacityResolved] = React.useState(false);
  const [memberAccess, setMemberAccess] = React.useState(normalizeProjectAccess());
  const [form, setForm] = React.useState(emptyForm);
  const [editingProduct, setEditingProduct] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [upgradeOpen, setUpgradeOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [productsLoaded, setProductsLoaded] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [processingImages, setProcessingImages] = React.useState(false);
  const [imagePreparationProgress, setImagePreparationProgress] = React.useState(null);
  const [imageUploadProgress, setImageUploadProgress] = React.useState(null);
  const [error, setError] = React.useState("");
  const [formFeedback, setFormFeedback] = React.useState(null);
  const [fieldErrors, setFieldErrors] = React.useState({});
  const formContentRef = React.useRef(null);
  const [imageError, setImageError] = React.useState("");
  const [selectedFiles, setSelectedFiles] = React.useState([]);
  const [previewUrls, setPreviewUrls] = React.useState([]);
  const previewUrlsRef = React.useRef([]);
  const autoCreateHandledRef = React.useRef("");
  const [existingImages, setExistingImages] = React.useState([]);
  const [removedImageIds, setRemovedImageIds] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [scannerOpen, setScannerOpen] = React.useState(false);
  const [createScannerOpen, setCreateScannerOpen] = React.useState(false);
  const [scanNotice, setScanNotice] = React.useState("");
  const [formCodeNotice, setFormCodeNotice] = React.useState("");
  const [typeFilter, setTypeFilter] = React.useState("all");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [categoryFilter, setCategoryFilter] = React.useState("all");
  const [subcategoryFilter, setSubcategoryFilter] = React.useState("all");
  const [supplierFilter, setSupplierFilter] = React.useState("all");
  const [importOpen, setImportOpen] = React.useState(false);
  const [bulkPriceOpen, setBulkPriceOpen] = React.useState(false);
  const [bulkPricePercent, setBulkPricePercent] = React.useState("");
  const [bulkPricePreview, setBulkPricePreview] = React.useState(null);
  const [bulkPriceSaving, setBulkPriceSaving] = React.useState(false);
  const [bulkPriceError, setBulkPriceError] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [toolsOpen, setToolsOpen] = React.useState(false);
  const [advancedFormOpen, setAdvancedFormOpen] = React.useState(false);

  React.useEffect(() => {
    if (!open) return undefined;
    const firstField = Object.keys(fieldErrors || {})[0];
    if (!firstField) return undefined;
    const timer = window.setTimeout(() => {
      const root = formContentRef.current;
      const field = root?.querySelector?.(`[name="${firstField}"]`);
      const target = field?.closest?.(".MuiFormControl-root") || field;
      target?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      field?.focus?.({ preventScroll: true });
    }, 90);
    return () => window.clearTimeout(timer);
  }, [advancedFormOpen, fieldErrors, open]);
  const [gastroStep, setGastroStep] = React.useState(0);
  const [gastroCategoryLinked, setGastroCategoryLinked] = React.useState(true);
  const [menuOrderOpen, setMenuOrderOpen] = React.useState(false);
  const [menuOrderSaving, setMenuOrderSaving] = React.useState(false);
  const [menuOrderError, setMenuOrderError] = React.useState("");
  const [menuOrderNotice, setMenuOrderNotice] = React.useState("");
  const [promotionDrafts, setPromotionDrafts] = React.useState([]);
  const [promotionLoading, setPromotionLoading] = React.useState(false);
  const [promotionError, setPromotionError] = React.useState("");
  const [savingStage, setSavingStage] = React.useState("");
  const [createdSupplyId, setCreatedSupplyId] = React.useState("");
  const initialFormSignatureRef = React.useRef("");
  const initialPromotionSignatureRef = React.useRef("[]");

  const project = React.useMemo(
    () =>
      projects.find(
        (item) => item.slug === projectId || item.id === projectId,
      ) || activeProject,
    [projects, projectId, activeProject],
  );
  const productsEnabled = Boolean(
    project && isProjectModuleEnabled(project, "products"),
  );
  const gastronomicMenuEnabled = Boolean(
    project && isProjectCapabilityEnabled(project, "gastronomicMenu"),
  );
  const membershipsEnabled = Boolean(
    project && isProjectCapabilityEnabled(project, "memberships"),
  );
  const recipesEnabled = Boolean(
    project && isProjectCapabilityEnabled(project, "recipes"),
  );
  const requestedGastronomyView = String(searchParams.get("vista") || "").trim().toLowerCase();
  const menuView = requestedGastronomyView === "carta" && gastronomicMenuEnabled;
  const recipesView = requestedGastronomyView === "recetas" && recipesEnabled;
  // Una vez que /products respondió, su payload `trial` es la fuente
  // autoritativa para este límite: el backend devuelve null cuando el proyecto
  // ya no está en prueba. Antes de esa respuesta conservamos el fallback del
  // proyecto para no cambiar el comportamiento durante la carga inicial.
  const isTrial = trialCapacityResolved ? trialCapacity !== null : isTrialProject(project);
  const trialItemsLimit = Number(trialCapacity?.itemsLimit || TRIAL_ITEMS_LIMIT);
  const trialItemsUsed = Number(trialCapacity?.itemsUsed ?? products.length);
  const trialItemsRemaining = Math.max(0, trialItemsLimit - trialItemsUsed);
  const trialLimitReached = isTrial && (trialCapacity?.limitReached === true || trialItemsUsed >= trialItemsLimit);
  const trialNeedsFirstPublication = isTrial && productsLoaded && products.length === 0;
  const billingPath = `/dashboard/proyectos/${projectId}/configuracion/suscripcion`;
  const landingPath = `/dashboard/proyectos/${projectId}/pagina`;

  const canManageCosts =
    hasProjectPermission(memberAccess, "items.costs.manage") ||
    hasProjectPermission(memberAccess, "financial.costs.manage");
  const canManageAccounting = hasProjectPermission(
    memberAccess,
    "items.accounting.manage",
  );
  const canCreateItems = hasProjectPermission(memberAccess, "items.create");
  const canUpdateItems = hasProjectPermission(memberAccess, "items.update");
  const canDeleteItems = hasProjectPermission(memberAccess, "items.delete");
  const canManageStock = hasProjectPermission(memberAccess, "erp.stock.manage");

  const categories = React.useMemo(
    () => uniqueClassificationValues(products.map((item) => productCategory(item))),
    [products],
  );

  const gastronomicSections = React.useMemo(
    () => uniqueClassificationValues(
      products
        .filter((item) => item.gastronomicMenu?.enabled === true)
        .map((item) => String(item.gastronomicMenu?.section || "").trim() || productCategory(item)),
    ),
    [products],
  );

  const effectiveGastronomicSection = String(form.gastronomicSection || "").trim() || String(form.category || "").trim();
  const guidedGastronomicForm = gastronomicMenuEnabled && ["product", "supply"].includes(form.erpItemKind);
  const gastronomicLastStep = form.erpItemKind === "supply" ? 3 : 5;

  const subcategoriesForCategory = React.useCallback(
    (category) =>
      uniqueClassificationValues(
        products
          .filter((item) => sameClassification(productCategory(item), category))
          .map((item) => productSubcategory(item)),
      ),
    [products],
  );

  const subcategories = React.useMemo(
    () =>
      categoryFilter === "all"
        ? []
        : subcategoriesForCategory(categoryFilter),
    [categoryFilter, subcategoriesForCategory],
  );

  const formSubcategories = React.useMemo(
    () => subcategoriesForCategory(form.category),
    [form.category, subcategoriesForCategory],
  );

  const supplierOptions = React.useMemo(() => {
    const values = products
      .map((item) => String(item?.inventory?.preferredSupplierId || "").trim())
      .filter(Boolean);
    return [...new Set(values)].sort();
  }, [products]);

  const filteredProducts = React.useMemo(() => {
    const query = normalizeSearchValue(searchTerm);

    return products.filter((product) => {
      const itemType = normalizeItemKind(product);
      const isActive = productIsActive(product);
      const category = productCategory(product);
      const subcategory = productSubcategory(product);
      const tags = tagsToText(product);
      const variantSearch = (product.variants || product.variations || [])
        .flatMap((variant) => [
          variant?.sku,
          variant?.barcode,
          ...Object.values(variant?.attributes || {}),
        ])
        .filter(Boolean)
        .join(" ");
      const searchableText = normalizeSearchValue(
        [
          productName(product),
          productDescription(product),
          category,
          subcategory,
          productCode(product),
          tags,
          variantSearch,
        ].join(" "),
      );

      const matchesSearch = !query || searchableText.includes(query);
      const matchesType = typeFilter === "all" || itemType === typeFilter;
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" ? isActive : !isActive);
      const matchesCategory =
        categoryFilter === "all" || sameClassification(category, categoryFilter);
      const matchesSubcategory =
        subcategoryFilter === "all" || sameClassification(subcategory, subcategoryFilter);
      const supplierId = String(product?.inventory?.preferredSupplierId || "").trim();
      const matchesSupplier = supplierFilter === "all" || supplierId === supplierFilter;
      const matchesGastronomyView = !menuView || product.gastronomicMenu?.enabled === true;

      return (
        matchesSearch &&
        matchesType &&
        matchesStatus &&
        matchesCategory &&
        matchesSubcategory &&
        matchesSupplier &&
        matchesGastronomyView
      );
    });
  }, [
    products,
    searchTerm,
    typeFilter,
    statusFilter,
    categoryFilter,
    subcategoryFilter,
    supplierFilter,
    menuView,
  ]);

  const paginatedProducts = React.useMemo(() => {
    const start = page * rowsPerPage;
    return filteredProducts.slice(start, start + rowsPerPage);
  }, [filteredProducts, page, rowsPerPage]);

  const inventorySummary = React.useMemo(() => {
    const active = products.filter(productIsActive).length;
    const services = products.filter(
      (product) => normalizeItemKind(product) === "service",
    ).length;
    const internal = products.filter((product) =>
      ["supply", "expense", "asset"].includes(normalizeItemKind(product)),
    ).length;
    const lowStock = products.filter((product) => {
      const capabilities = productCapabilities(product);
      const threshold = Number(product.inventory?.reorderPoint ?? 5);
      return capabilities.stockTracked && productStock(product) <= threshold;
    }).length;

    return {
      total: products.length,
      active,
      services,
      internal,
      lowStock,
    };
  }, [products]);

  const inventorySummaryCards = (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" },
        gap: 2,
      }}
    >
      {[
        { label: "Ítems totales", value: inventorySummary.total },
        { label: "Activos", value: inventorySummary.active },
        { label: "Uso interno", value: inventorySummary.internal },
        { label: "Stock bajo", value: inventorySummary.lowStock },
      ].map((item) => (
        <Paper key={item.label} elevation={0} sx={{ p: { xs: 1.5, sm: 2.5 }, borderRadius: 3, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),0.08)", bgcolor: "var(--wp-panel-surface, #fff)" }}>
          <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", fontWeight: 750 }}>{item.label}</Typography>
          <Typography variant="h5" sx={{ color: BRAND.dark, fontWeight: 950, mt: 0.35 }}>{item.value}</Typography>
        </Paper>
      ))}
    </Box>
  );

  const hasActiveFilters = Boolean(
    searchTerm ||
    typeFilter !== "all" ||
    statusFilter !== "all" ||
    categoryFilter !== "all" ||
    subcategoryFilter !== "all" ||
    supplierFilter !== "all",
  );

  React.useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  React.useEffect(() => {
    let mounted = true;
    projectsApi
      .get(projectId)
      .then((data) => {
        if (!mounted) return;
        setMemberAccess(normalizeProjectAccess(data.memberAccess || data.member || {}));
        if (data.project) setActiveProject(data.project);
      })
      .catch(() => {
        if (mounted) setMemberAccess(normalizeProjectAccess());
      });
    return () => {
      mounted = false;
    };
  }, [projectId, setActiveProject]);

  React.useEffect(() => {
    if (projectId && (!activeProject || activeProject.slug !== projectId)) {
      const found = projects.find(
        (item) => item.slug === projectId || item.id === projectId,
      );
      if (found) setActiveProject(found);
    }
  }, [projectId, projects, activeProject, setActiveProject]);

  React.useEffect(() => {
    previewUrlsRef.current = previewUrls;
  }, [previewUrls]);

  React.useEffect(
    () => () => {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    },
    [],
  );

  const loadProducts = React.useCallback(async () => {
    if (!projectId || !project || !productsEnabled) {
      setLoading(false);
      setProductsLoaded(true);
      return;
    }
    try {
      setProductsLoaded(false);
      setLoading(true);
      setError("");
      const data = await productsApi.list(projectId);
      const nextProducts = Array.isArray(data?.products) ? data.products : [];
      setProducts(nextProducts);
      setTrialCapacity(data?.trial || null);
      setTrialCapacityResolved(true);
      window.dispatchEvent(
        new CustomEvent(TRIAL_PRODUCTS_EVENT, {
          detail: { projectId, count: nextProducts.length },
        }),
      );
    } catch (err) {
      setProducts([]);
      setTrialCapacity(null);
      setTrialCapacityResolved(false);
      setError(err.message || "No se pudieron cargar las publicaciones");
    } finally {
      setLoading(false);
      setProductsLoaded(true);
    }
  }, [projectId, project, productsEnabled]);

  React.useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const refreshRecipeSummaries = React.useCallback(async () => {
    if (!projectId || !recipesEnabled) {
      setRecipeSummaries([]);
      return;
    }
    try {
      setRecipesLoading(true);
      const data = await recipesApi.list(projectId);
      setRecipeSummaries(Array.isArray(data?.recipes) ? data.recipes : []);
    } catch (err) {
      setRecipeSummaries([]);
      setError((current) => current || err.message || "No se pudieron cargar las recetas");
    } finally {
      setRecipesLoading(false);
    }
  }, [projectId, recipesEnabled]);

  React.useEffect(() => {
    refreshRecipeSummaries();
  }, [refreshRecipeSummaries]);

  const recipeSummaryByProduct = React.useMemo(
    () => new Map(recipeSummaries.map((recipe) => [String(recipe.productId), recipe])),
    [recipeSummaries],
  );

  const openRecipeForProduct = React.useCallback((product) => {
    const id = productId(product);
    if (!id) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("vista", "recetas");
    nextParams.set("producto", id);
    setSearchParams(nextParams);
  }, [searchParams, setSearchParams]);

  const clearRecipeSelection = React.useCallback(() => {
    const nextParams = new URLSearchParams(searchParams);
    if (!nextParams.has("producto")) return;
    nextParams.delete("producto");
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const manageRecipeSupplies = React.useCallback(() => {
    setTypeFilter("supply");
    setStatusFilter("all");
    setSearchTerm("");
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("vista");
    nextParams.delete("producto");
    setSearchParams(nextParams);
  }, [searchParams, setSearchParams]);

  React.useEffect(() => {
    setPage(0);
  }, [
    searchTerm,
    typeFilter,
    statusFilter,
    categoryFilter,
    subcategoryFilter,
    rowsPerPage,
  ]);

  React.useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(filteredProducts.length / rowsPerPage) - 1,
    );
    if (page > maxPage) setPage(maxPage);
  }, [filteredProducts.length, page, rowsPerPage]);

  const resetUploads = React.useCallback(() => {
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    previewUrlsRef.current = [];
    setSelectedFiles([]);
    setPreviewUrls([]);
    setExistingImages([]);
    setRemovedImageIds([]);
    setImagePreparationProgress(null);
    setImageUploadProgress(null);
    setImageError("");
  }, []);

  React.useEffect(() => {
    if (!open) return;
    initialFormSignatureRef.current = JSON.stringify(form);
    initialPromotionSignatureRef.current = JSON.stringify([]);
    // Sólo al abrir: los cambios posteriores quedan detectados como borrador.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadPromotionDrafts = React.useCallback(async (targetProductId) => {
    if (!targetProductId || !gastronomicMenuEnabled) {
      setPromotionDrafts([]);
      setPromotionError("");
      return;
    }
    setPromotionLoading(true);
    setPromotionError("");
    try {
      const response = await promotionsApi.list(projectId);
      const related = (response?.promotions || []).filter((promotion) =>
        (promotion.lines || []).some((line) => String(line.productId) === String(targetProductId)),
      );
      const drafts = related.map(promotionToDraft);
      setPromotionDrafts(drafts);
      initialPromotionSignatureRef.current = JSON.stringify(drafts);
    } catch (err) {
      setPromotionDrafts([]);
      setPromotionError(err?.message || "No se pudieron cargar las promociones de este producto.");
    } finally {
      setPromotionLoading(false);
    }
  }, [gastronomicMenuEnabled, projectId]);

  const handleClose = () => {
    if (saving || processingImages) return;
    const dirty = Boolean(
      selectedFiles.length
      || removedImageIds.length
      || JSON.stringify(form) !== initialFormSignatureRef.current
      || JSON.stringify(promotionDrafts) !== initialPromotionSignatureRef.current
    );
    if (dirty && !createdSupplyId) {
      const confirmed = window.confirm("Hay cambios sin guardar. ¿Querés cerrar y descartarlos?");
      if (!confirmed) return;
    }
    setOpen(false);
    setFormFeedback(null);
    setFieldErrors({});
    setFormCodeNotice("");
    setCreateScannerOpen(false);
    setGastroStep(0);
    setGastroCategoryLinked(true);
    setPromotionDrafts([]);
    setPromotionError("");
    setSavingStage("");
    setCreatedSupplyId("");
    resetUploads();
  };

  const requestUpgrade = React.useCallback(() => {
    setUpgradeOpen(true);
  }, []);

  const openCreateWithType = React.useCallback(
    (itemKind = "product") => {
      if (!canCreateItems) {
        setError("Tu rol no tiene permiso para crear ítems.");
        return;
      }
      if (trialLimitReached) {
        requestUpgrade();
        return;
      }
      const normalizedKind = ITEM_KIND_LABELS[itemKind] ? itemKind : "product";
      const capabilities = capabilitiesForKind(normalizedKind);
      setEditingProduct(null);
      setForm({
        ...emptyForm,
        erpItemKind: normalizedKind,
        itemType: normalizedKind === "service" ? "service" : "product",
        ...capabilities,
        requiresBooking: Boolean(capabilities.reservable),
        requiresShipping: Boolean(capabilities.shippable),
        attributes: [],
        ...(gastronomicMenuEnabled && normalizedKind === "product"
          ? { category: "", gastronomicEnabled: true, gastronomicAvailable: true }
          : gastronomicMenuEnabled && normalizedKind === "supply"
            ? { category: "Insumos", gastronomicEnabled: false }
            : { gastronomicEnabled: false }),
      });
      setGastroStep(0);
      setGastroCategoryLinked(gastronomicMenuEnabled && normalizedKind === "product");
      setPromotionDrafts([]);
      setPromotionError("");
      setCreatedSupplyId("");
      resetUploads();
      setError("");
      setFormFeedback(null);
      setFieldErrors({});
      setFormCodeNotice("");
      setAdvancedFormOpen(false);
      setOpen(true);
    },
    [canCreateItems, gastronomicMenuEnabled, requestUpgrade, resetUploads, trialLimitReached],
  );

  const handleOpenCreate = React.useCallback(() => {
    openCreateWithType(recipesView ? "supply" : "product");
  }, [openCreateWithType, recipesView]);

  React.useEffect(() => {
    const shouldCreate = searchParams.get("crear") === "1";
    if (!shouldCreate || !project || !productsEnabled || loading || !productsLoaded) return;

    const requestKey = `${projectId}:${searchParams.toString()}`;
    if (autoCreateHandledRef.current === requestKey) return;
    autoCreateHandledRef.current = requestKey;

    const requestedKind = ITEM_KIND_LABELS[searchParams.get("tipo")]
      ? searchParams.get("tipo")
      : "product";
    openCreateWithType(requestedKind);

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("crear");
    nextParams.delete("tipo");
    setSearchParams(nextParams, { replace: true });
  }, [
    loading,
    productsLoaded,
    openCreateWithType,
    productsEnabled,
    project,
    projectId,
    searchParams,
    setSearchParams,
  ]);

  const handleOpenEdit = (product) => {
    if (!canUpdateItems) {
      setError("Tu rol no tiene permiso para editar ítems.");
      return;
    }
    setEditingProduct(product);
    const itemType = normalizeItemType(product);
    const erpItemKind = normalizeItemKind(product);
    const capabilities = productCapabilities(product);
    setForm({
      itemType,
      erpItemKind,
      name: product.name || product.nombre || "",
      description: product.description || product.descripcion || "",
      category: product.category || product.categoria || "General",
      subcategory: product.subcategory || product.subcategoria || "",
      gastronomicEnabled: product.gastronomicMenu?.enabled === true,
      gastronomicAvailable: product.gastronomicMenu?.available !== false,
      gastronomicShortDescription: product.gastronomicMenu?.shortDescription || "",
      gastronomicDietaryNotes: product.gastronomicMenu?.dietaryNotes || "",
      gastronomicSection: product.gastronomicMenu?.section || "",
      gastronomicSectionOrder: String(product.gastronomicMenu?.sectionOrder ?? 0),
      gastronomicSortOrder: String(product.gastronomicMenu?.sortOrder ?? 0),
      gastronomicFeatured: product.gastronomicMenu?.featured === true,
      code: product.code || product.codigo || "",
      unit: product.unit || product.unidad || "",
      priceType: normalizePriceType(product),
      price: String(product.price ?? product.precio ?? 0),
      stock: String(product.stock ?? 0),
      durationMinutes: String(
        product.durationMinutes ?? product.duracionMinutos ?? "",
      ),
      modality: product.modality || product.modalidad || "",
      requiresBooking: Boolean(capabilities.reservable),
      requiresShipping: Boolean(capabilities.shippable),
      bookingProvider:
        product.bookingProvider === "calendly"
          ? "calendly"
          : Boolean(capabilities.reservable)
            ? "webprofit"
            : "none",
      calendlyUrl: product.calendlyUrl || "",
      calendlyDisplayMode: product.calendlyDisplayMode === "inline" ? "inline" : "popup",
      bookingConfirmationMode:
        product.bookingConfirmationMode ||
        (product.confirmacionTurno === "automatica" ? "automatic" : "manual"),
      bookingPaymentMode:
        product.bookingPaymentMode || product.pagoReserva || "none",
      bookingDepositType:
        product.bookingDepositType || product.tipoSena || "fixed",
      bookingDepositValue: String(
        product.bookingDepositValue ?? product.valorSena ?? 0,
      ),
      capacity: String(product.capacity ?? product.capacidad ?? 1),
      bufferBeforeMinutes: String(
        product.bufferBeforeMinutes ?? product.bufferAntesMinutos ?? 0,
      ),
      bufferAfterMinutes: String(
        product.bufferAfterMinutes ?? product.bufferDespuesMinutos ?? 0,
      ),
      serviceLocation:
        product.serviceLocation || product.ubicacionServicio || "",
      meetingUrl: product.meetingUrl || product.enlaceVideollamada || "",
      bookingInstructions:
        product.bookingInstructions || product.instruccionesReserva || "",
      cancellationWindowHours: String(
        product.cancellationWindowHours ?? product.cancelacionHoras ?? 0,
      ),
      tags: tagsToText(product),
      attributes: attributesToRows(product.attributes),
      hasVariants: Boolean(
        product.hasVariants ??
          ((product.variantAttributes || product.variantOptions || []).length &&
            (product.variants || product.variations || []).length),
      ),
      variantAttributes: variantAttributesToForm(product),
      variants: variantsToForm(product),
      quantityPriceTiers: quantityPriceTiersToForm(product),
      active: Boolean(product.active ?? product.activo ?? true),
      membershipEligible: product.operational?.membershipEligible === true,
      membershipPeriodValue: String(product.membershipPlan?.periodValue ?? 1),
      membershipPeriodUnit: product.membershipPlan?.periodUnit || "months",
      membershipReferencePrice: String(product.membershipPlan?.referencePrice ?? product.price ?? product.precio ?? 0),
      membershipUsageEnabled: product.membershipPlan?.usageEnabled === true,
      membershipIncludedUses: product.membershipPlan?.includedUses == null ? "" : String(product.membershipPlan.includedUses),
      membershipUsageLabel: product.membershipPlan?.usageLabel || "usos",
      ...capabilities,
      lastCost: String(product.costing?.lastCost ?? 0),
      averageCost: String(product.costing?.averageCost ?? 0),
      standardCost: String(product.costing?.standardCost ?? 0),
      costingMethod: product.costing?.costingMethod || "last_cost",
      costCurrency: product.costing?.currency || "ARS",
      allowNegativeStock: product.inventory?.allowNegativeStock === true,
      minimumStock: String(product.inventory?.minimumStock ?? 0),
      reorderPoint: String(product.inventory?.reorderPoint ?? 0),
      inventoryLocation: product.inventory?.location || "",
      salesAccountCode: product.accounting?.salesAccountCode || "SALES",
      purchaseAccountCode: product.accounting?.purchaseAccountCode || "PURCHASES",
      inventoryAccountCode: product.accounting?.inventoryAccountCode || "INVENTORY",
      costAccountCode: product.accounting?.costAccountCode || "COST_OF_GOODS",
      expenseAccountCode: product.accounting?.expenseAccountCode || "OTHER_EXPENSE",
      taxProfileCode: product.tax?.profileCode || "",
      taxRate: String(product.tax?.rate ?? 0),
      taxIncludedInPrice: product.tax?.includedInPrice === true,
    });
    resetUploads();
    setFormFeedback(null);
    setFieldErrors({});
    setFormCodeNotice("");

    const resolvedUrls = getProductImageUrls(product);
    const images = (product.images || [])
      .map((img, index) => ({
        identifier:
          (typeof img === "string"
            ? img
            : img?.path || img?.filename || img?.url) || "",
        url:
          resolvedUrls[index] ||
          (typeof img === "string" ? img : img?.url || img?.path) ||
          "",
      }))
      .filter((img) => img.identifier && img.url);
    setExistingImages(images.slice(0, MAX_IMAGES));
    setGastroStep(0);
    // En edición no sincronizamos categoría y sección de forma implícita:
    // los documentos históricos conservan exactamente su clasificación actual.
    setGastroCategoryLinked(false);
    setPromotionDrafts([]);
    setPromotionError("");
    setCreatedSupplyId("");
    setAdvancedFormOpen(Boolean(
      String(product.code || product.codigo || "").trim()
      || String(product.subcategory || product.subcategoria || "").trim()
      || String(product.unit || product.unidad || "").trim()
      || (product.variants || product.variations || []).length
      || tagsToText(product)
      || attributesToRows(product.attributes).length
      || Number(product.costing?.lastCost || product.costing?.averageCost || product.costing?.standardCost || 0) > 0
      || String(product.tax?.profileCode || "").trim()
    ));
    setOpen(true);
    if (erpItemKind === "product" && gastronomicMenuEnabled) loadPromotionDrafts(productId(product));
  };

  const handleRemoveExistingImage = (identifier) => {
    setExistingImages((prev) =>
      prev.filter((img) => img.identifier !== identifier),
    );
    setRemovedImageIds((prev) =>
      prev.includes(identifier) ? prev : [...prev, identifier],
    );
    setImageError("");
  };

  const handleFilesChange = async (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = "";
    if (!picked.length) return;

    const available = Math.max(
      0,
      MAX_IMAGES - existingImages.length - selectedFiles.length,
    );
    if (!available) {
      setImageError(`La publicación ya tiene ${MAX_IMAGES} imágenes.`);
      return;
    }

    const accepted = picked.slice(0, available);
    if (picked.length > available) {
      setImageError(
        `Se tomarán ${available} archivo${available === 1 ? "" : "s"}. El máximo total es ${MAX_IMAGES}.`,
      );
    } else {
      setImageError("");
    }

    try {
      setProcessingImages(true);
      setImagePreparationProgress(0);
      const optimized = await optimizeProductImages(accepted, {
        onProgress: ({ percent }) => setImagePreparationProgress(percent),
      });
      const urls = optimized.map((file) => URL.createObjectURL(file));
      setSelectedFiles((prev) => [...prev, ...optimized]);
      setPreviewUrls((prev) => [...prev, ...urls]);
    } catch (err) {
      setImageError(err.message || "No se pudieron preparar las imágenes.");
    } finally {
      setProcessingImages(false);
      setImagePreparationProgress(null);
    }
  };

  const handleRemoveSelectedFile = (index) => {
    setSelectedFiles((prev) =>
      prev.filter((_, itemIndex) => itemIndex !== index),
    );
    setPreviewUrls((prev) => {
      const url = prev[index];
      if (url) URL.revokeObjectURL(url);
      return prev.filter((_, itemIndex) => itemIndex !== index);
    });
    setImageError("");
  };

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (name && fieldErrors[name]) {
      setFieldErrors((current) => {
        const next = { ...current };
        delete next[name];
        return next;
      });
    }
  };

  const handleGastronomicRoleChange = React.useCallback((nextKind) => {
    const normalizedKind = ITEM_KIND_LABELS[nextKind] ? nextKind : "product";
    if (editingProduct && normalizedKind !== form.erpItemKind) {
      const confirmed = window.confirm(
        "Cambiar el tipo puede modificar capacidades operativas del ítem. Los datos existentes se conservarán siempre que sigan siendo compatibles. ¿Querés continuar?",
      );
      if (!confirmed) return;
    }

    setForm((current) => {
      const nextCapabilities = capabilitiesForKind(normalizedKind);
      const isService = normalizedKind === "service";
      const supportsVariants = supportsVariantsForKind(normalizedKind);
      const isGuidedProduct = normalizedKind === "product";
      const isSupply = normalizedKind === "supply";
      return {
        ...current,
        erpItemKind: normalizedKind,
        itemType: isService ? "service" : "product",
        ...nextCapabilities,
        requiresBooking: Boolean(nextCapabilities.reservable),
        requiresShipping: Boolean(nextCapabilities.shippable),
        gastronomicEnabled: isGuidedProduct ? true : isSupply ? false : current.gastronomicEnabled,
        gastronomicAvailable: isGuidedProduct ? current.gastronomicAvailable !== false : current.gastronomicAvailable,
        ...(!editingProduct && isSupply ? { category: "Insumos", subcategory: "" } : {}),
        ...(!editingProduct && isGuidedProduct && current.erpItemKind !== "product" ? { category: "", subcategory: "" } : {}),
        stock: nextCapabilities.stockTracked ? current.stock : "0",
        durationMinutes: isService ? current.durationMinutes : "",
        modality: isService ? current.modality : "",
        hasVariants: supportsVariants ? current.hasVariants : false,
        variantAttributes: supportsVariants ? current.variantAttributes : [],
        variants: supportsVariants ? current.variants : [],
      };
    });
    setGastroStep(0);
    if (!editingProduct && normalizedKind === "product") setGastroCategoryLinked(true);
    if (normalizedKind !== "product") setGastroCategoryLinked(false);
  }, [editingProduct, form.erpItemKind]);

  const handleGastronomicSectionChange = React.useCallback((value) => {
    const nextSection = String(value || "");
    const matching = products.filter((item) => sameClassification(
      String(item.gastronomicMenu?.section || "").trim() || productCategory(item),
      nextSection,
    ));
    const existingSectionOrder = matching.length
      ? Number(matching[0]?.gastronomicMenu?.sectionOrder || 0)
      : Math.max(0, ...products.map((item) => Number(item.gastronomicMenu?.sectionOrder || 0))) + 10;
    const nextSortOrder = matching.length
      ? Math.max(0, ...matching.map((item) => Number(item.gastronomicMenu?.sortOrder || 0))) + 10
      : 0;
    setForm((current) => ({
      ...current,
      gastronomicSection: nextSection,
      ...(!editingProduct || !sameClassification(current.gastronomicSection || current.category, nextSection)
        ? { gastronomicSectionOrder: String(existingSectionOrder), gastronomicSortOrder: String(nextSortOrder) }
        : {}),
      ...(gastroCategoryLinked
        ? {
            category: nextSection || current.category,
            subcategory: sameClassification(current.category, nextSection) ? current.subcategory : "",
          }
        : {}),
    }));
    if (fieldErrors.category) {
      setFieldErrors((current) => {
        const next = { ...current };
        delete next.category;
        return next;
      });
    }
  }, [editingProduct, fieldErrors.category, gastroCategoryLinked, products]);

  const handleGastronomicCategoryChange = React.useCallback((value) => {
    const nextCategory = String(value || "");
    setGastroCategoryLinked(false);
    setForm((current) => ({
      ...current,
      category: nextCategory,
      subcategory: sameClassification(current.category, nextCategory) ? current.subcategory : "",
    }));
  }, []);

  const handleAttributeChange = (index, key, value) => {
    setForm((prev) => ({
      ...prev,
      attributes: prev.attributes.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [key]: value } : row,
      ),
    }));
  };

  const addAttribute = () => {
    setForm((prev) => ({
      ...prev,
      attributes: [...prev.attributes, { label: "", value: "" }].slice(0, 30),
    }));
  };

  const removeAttribute = (index) => {
    setForm((prev) => ({
      ...prev,
      attributes: prev.attributes.filter((_, rowIndex) => rowIndex !== index),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (processingImages) return;

    const editingId = productId(editingProduct);
    const creating = !editingId;
    if ((creating && !canCreateItems) || (!creating && !canUpdateItems)) {
      setFormFeedback({
        kind: "access",
        title: "No tenés permiso para guardar",
        message: "Tu rol no tiene permiso para guardar este ítem.",
        fields: {},
      });
      formContentRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
      return;
    }
    if (creating && trialLimitReached) {
      setOpen(false);
      setFormFeedback(null);
      setFieldErrors({});
      resetUploads();
      requestUpgrade();
      return;
    }

    const basicFieldErrors = validateBasicItemFields(form);
    if (Object.keys(basicFieldErrors).length) {
      if (Object.keys(basicFieldErrors).some((field) => !["name", "category", "price"].includes(field))) {
        setAdvancedFormOpen(true);
      }
      setFieldErrors(basicFieldErrors);
      setFormFeedback(
        validationFeedback(
          "Completá los campos marcados antes de guardar.",
          basicFieldErrors,
        ),
      );
      if (guidedGastronomicForm) {
        setGastroStep(basicFieldErrors.price && !basicFieldErrors.name && !basicFieldErrors.category ? 2 : basicFieldErrors.category && !basicFieldErrors.name ? 1 : 0);
      }
      formContentRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
      return;
    }

    const variantError = validateVariantForm(form);
    if (variantError) {
      setAdvancedFormOpen(true);
      setFormFeedback(validationFeedback(variantError));
      if (guidedGastronomicForm && form.erpItemKind === "product") setGastroStep(3);
      formContentRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
      return;
    }

    const quantityDiscountError = validateQuantityDiscountRows(form);
    if (quantityDiscountError) {
      setAdvancedFormOpen(true);
      setFormFeedback(validationFeedback(quantityDiscountError));
      if (guidedGastronomicForm && form.erpItemKind === "product") setGastroStep(4);
      formContentRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
      return;
    }

    if (form.erpItemKind === "product") {
      const promotionDraftError = validatePromotionDrafts(promotionDrafts, editingId);
      if (promotionDraftError) {
        setAdvancedFormOpen(true);
        setFormFeedback(validationFeedback(promotionDraftError));
        if (guidedGastronomicForm) setGastroStep(4);
        formContentRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
        return;
      }
    }

    try {
      setSaving(true);
      setSavingStage("product");
      setError("");
      setFormFeedback(null);
      setFieldErrors({});
      const normalizedCategory =
        reuseClassificationValue(form.category, categories) || "General";
      const normalizedSubcategory = reuseClassificationValue(
        form.subcategory,
        subcategoriesForCategory(normalizedCategory),
      );
      const payload = normalizeProductPayload(
        {
          ...form,
          category: normalizedCategory,
          subcategory: normalizedSubcategory,
        },
        variantImageOptions(existingImages, previewUrls),
        { membershipsEnabled },
      );
      if (gastronomicMenuEnabled) {
        payload.gastronomicMenu = {
          enabled: Boolean(form.gastronomicEnabled),
          available: Boolean(form.gastronomicAvailable),
          shortDescription: String(form.gastronomicShortDescription || "").trim(),
          dietaryNotes: String(form.gastronomicDietaryNotes || "").trim(),
          section: reuseClassificationValue(form.gastronomicSection, gastronomicSections),
          sectionOrder: Number(form.gastronomicSectionOrder || 0),
          sortOrder: Number(form.gastronomicSortOrder || 0),
          featured: Boolean(form.gastronomicFeatured),
        };
      }
      let savedProduct = null;
      const uploadOptions = selectedFiles.length
        ? {
            onUploadProgress: (progressEvent) => {
              const percent = uploadProgressPercent(progressEvent);
              if (percent !== null) setImageUploadProgress(percent);
            },
          }
        : {};
      setImageUploadProgress(selectedFiles.length ? 0 : null);
      if (editingId) {
        if (removedImageIds.length) payload.removeImages = removedImageIds;
        const response = await productsApi.update(
          projectId,
          editingId,
          payload,
          selectedFiles,
          uploadOptions,
        );
        savedProduct = response?.product || null;
      } else {
        const response = await productsApi.create(
          projectId,
          payload,
          selectedFiles,
          uploadOptions,
        );
        savedProduct = response?.product || null;
      }
      const savedId = productId(savedProduct) || editingId;
      const promotionsChanged = JSON.stringify(promotionDrafts) !== initialPromotionSignatureRef.current;
      if (form.erpItemKind === "product" && promotionDrafts.length && promotionsChanged) {
        setSavingStage("promotions");
        let persistedDrafts = [...promotionDrafts];
        try {
          for (let index = 0; index < persistedDrafts.length; index += 1) {
            const draft = persistedDrafts[index];
            if (draft._delete) {
              if (draft.id) await promotionsApi.remove(projectId, draft.id);
              persistedDrafts = persistedDrafts.filter((_, currentIndex) => currentIndex !== index);
              index -= 1;
              setPromotionDrafts(persistedDrafts);
              continue;
            }
            const promotionPayload = promotionPayloadFromDraft(draft, savedId);
            const response = draft.id
              ? await promotionsApi.update(projectId, draft.id, promotionPayload)
              : await promotionsApi.create(projectId, promotionPayload);
            const persisted = promotionToDraft(response?.promotion || { ...promotionPayload, id: draft.id });
            persistedDrafts[index] = persisted;
            setPromotionDrafts([...persistedDrafts]);
          }
          initialPromotionSignatureRef.current = JSON.stringify(persistedDrafts);
        } catch (promotionSaveError) {
          setPromotionDrafts(persistedDrafts);
          setEditingProduct(savedProduct || { ...editingProduct, id: savedId, _id: savedId });
          initialFormSignatureRef.current = JSON.stringify(form);
          setPromotionError(promotionSaveError?.message || "El producto se guardó, pero una promoción no pudo guardarse.");
          setFormFeedback({
            kind: "warning",
            title: "Producto guardado · promoción pendiente",
            message: "El producto ya existe y no se volverá a crear. Lo que alcanzó a guardarse no se duplicará; revisá lo pendiente e intentá nuevamente.",
            detail: promotionSaveError?.message || "No se pudo guardar la promoción.",
            fields: {},
          });
          setGastroStep(4);
          resetUploads();
          await loadProducts();
          formContentRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
          return;
        }
      }

      resetUploads();
      await loadProducts();

      if (creating && form.erpItemKind === "supply") {
        setEditingProduct(savedProduct || { id: savedId, _id: savedId });
        setCreatedSupplyId(savedId);
        initialFormSignatureRef.current = JSON.stringify(form);
        initialPromotionSignatureRef.current = JSON.stringify(promotionDrafts);
        setGastroStep(3);
        setFormFeedback({
          kind: "success",
          title: "Insumo creado",
          message: canManageStock
            ? "Ya podés registrar su stock inicial desde Movimientos."
            : "El insumo quedó creado. Un usuario con permiso de stock puede cargar la existencia inicial desde Movimientos.",
          fields: {},
        });
        return;
      }

      setOpen(false);

      if (creating && isReservableService(form) && form.bookingProvider !== "calendly") {
        const savedId = productId(savedProduct);
        navigate(
          `/dashboard/proyectos/${projectId}/agenda${savedId ? `?servicio=${savedId}&nuevo=1` : ""}`,
        );
      }
    } catch (err) {
      setFormFeedback(
        saveErrorFeedback(err, { hasImages: selectedFiles.length > 0 }),
      );
      formContentRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
    } finally {
      setSaving(false);
      setSavingStage("");
      setImageUploadProgress(null);
    }
  };

  const handleDelete = async (product) => {
    if (!canDeleteItems) {
      setError("Tu rol no tiene permiso para eliminar ítems.");
      return;
    }
    const label =
      normalizeItemType(product) === "service" ? "servicio" : "producto";
    const ok = window.confirm(`¿Borrar el ${label} "${productName(product)}"?`);
    if (!ok) return;
    try {
      setError("");
      await productsApi.remove(projectId, productId(product));
      await loadProducts();
    } catch (err) {
      setError(err.message || "No se pudo borrar la publicación");
    }
  };

  const handleScannedItemCode = React.useCallback(async (rawCode) => {
    const code = String(rawCode || "").trim();
    if (!code) return;
    const data = await productsApi.lookupCode(projectId, code);
    const found = data?.product;
    if (!found) throw new Error(`No se encontró ningún ítem con el código ${code}.`);

    setProducts((current) => {
      const foundId = productId(found);
      const exists = current.some((item) => productId(item) === foundId);
      return exists
        ? current.map((item) => (productId(item) === foundId ? found : item))
        : [found, ...current];
    });
    setTypeFilter("all");
    setStatusFilter("all");
    setCategoryFilter("all");
    setSubcategoryFilter("all");
    setSupplierFilter("all");
    setSearchTerm(code);
    setPage(0);
    setScanNotice(`Encontrado: ${productName(found)}${productCode(found) ? ` · ${productCode(found)}` : ""}`);
  }, [projectId]);

  const handleScannedCreateCode = React.useCallback(async (rawCode) => {
    const code = String(rawCode || "").trim();
    if (!code) return;
    try {
      await productsApi.lookupCode(projectId, code);
      throw new Error(`El código ${code} ya está asignado a otro ítem o variante.`);
    } catch (error) {
      if (error?.code !== "ITEM_CODE_NOT_FOUND" && error?.status !== 404) throw error;
    }

    setForm((current) => ({ ...current, code }));
    setFieldErrors((current) => ({ ...current, code: "" }));
    setFormCodeNotice(`Código capturado: ${code}. Se guardará únicamente al crear este ítem.`);
  }, [projectId]);

  const handleDownloadCodes = React.useCallback((items, filename = "webprofit-codigos.pdf") => {
    try {
      const result = downloadBarcodeLabelsPdf(items, filename);
      setScanNotice(`PDF generado: ${result.labels} etiqueta${result.labels === 1 ? "" : "s"} lista${result.labels === 1 ? "" : "s"} para imprimir.`);
    } catch (error) {
      setError(error?.message || "No se pudieron generar las etiquetas.");
    }
  }, []);

  const handleDownloadCurrentFormCode = React.useCallback(() => {
    const syntheticProduct = {
      name: form.name || "Ítem",
      code: form.code,
      variants: Array.isArray(form.variants) ? form.variants : [],
    };
    const base = String(form.name || form.code || "item").trim() || "item";
    handleDownloadCodes([syntheticProduct], `webprofit-${base}-codigos.pdf`);
  }, [form.code, form.name, form.variants, handleDownloadCodes]);

  const openBulkPriceAdjustment = () => {
    setBulkPricePercent("");
    setBulkPricePreview(null);
    setBulkPriceError("");
    setBulkPriceOpen(true);
  };

  const bulkPricePayload = (dryRun) => ({
    percentage: Number(bulkPricePercent),
    dryRun,
    productIds: filteredProducts
      .map((item) => productId(item))
      .filter((value) => /^[a-f0-9]{24}$/i.test(String(value || "")))
      .slice(0, 500),
  });

  const previewBulkPriceAdjustment = async () => {
    const percentage = Number(bulkPricePercent);
    if (!Number.isFinite(percentage) || percentage === 0 || percentage <= -100) {
      setBulkPriceError("Ingresá un porcentaje válido distinto de 0 y mayor a -100.");
      return;
    }
    try {
      setBulkPriceSaving(true);
      setBulkPriceError("");
      const result = await productsApi.bulkAdjustPrices(projectId, bulkPricePayload(true));
      setBulkPricePreview(result);
    } catch (requestError) {
      setBulkPriceError(requestError?.message || "No se pudo calcular la vista previa.");
    } finally {
      setBulkPriceSaving(false);
    }
  };

  const applyBulkPriceAdjustment = async () => {
    try {
      setBulkPriceSaving(true);
      setBulkPriceError("");
      const result = await productsApi.bulkAdjustPrices(projectId, bulkPricePayload(false));
      await loadProducts();
      setBulkPriceOpen(false);
      setScanNotice(`Precios actualizados: ${result?.modified ?? result?.matched ?? 0} ítem(s).`);
    } catch (requestError) {
      setBulkPriceError(requestError?.message || "No se pudo aplicar el ajuste masivo.");
    } finally {
      setBulkPriceSaving(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setScanNotice("");
    setTypeFilter("all");
    setStatusFilter("all");
    setCategoryFilter("all");
    setSubcategoryFilter("all");
    setSupplierFilter("all");
  };

  const handleSaveMenuOrder = React.useCallback(async (updates = []) => {
    if (!canUpdateItems || menuOrderSaving) return;
    setMenuOrderSaving(true);
    setMenuOrderError("");
    setMenuOrderNotice("");
    try {
      for (const update of updates) {
        if (!update.id) continue;
        await productsApi.update(projectId, update.id, { gastronomicMenu: update.gastronomicMenu });
      }
      await loadProducts();
      setMenuOrderNotice("Orden de la Carta guardado.");
      setMenuOrderOpen(false);
    } catch (orderError) {
      await loadProducts();
      setMenuOrderError(orderError?.message || "No se pudo guardar todo el orden. Se recargó el estado real para evitar mostrar un éxito falso.");
    } finally {
      setMenuOrderSaving(false);
    }
  }, [canUpdateItems, loadProducts, menuOrderSaving, projectId]);

  if (project && !productsEnabled) {
    return (
      <ModuleDisabledState
        projectId={projectId}
        title="Ítems del negocio"
        description="Este módulo unifica catálogo, servicios, insumos, gastos, stock y conceptos internos."
      />
    );
  }

  if (loading && !productsLoaded) {
    return <DashboardLoadingState label="Cargando ítems del negocio…" minHeight={420} />;
  }

  return (
    <Stack spacing={3} sx={{ pb: 4 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ md: "center" }}
        justifyContent="space-between"
        spacing={2}
      >
        <EditorialIntro
          title={menuView ? "Carta gastronómica" : recipesView ? "Recetas e insumos" : "Ítems del negocio"}
          context={`${project?.name || projectId} · ${menuView
            ? "ítems de la carta pública"
            : recipesView
              ? "recetas e insumos gastronómicos"
              : "catálogo y operación interna"}.`}
          helpTitle="Cómo elegir el tipo de ítem"
          helpText="Usá el tipo que mejor describa lo que administrás. El formulario adapta los campos para pedir sólo la información necesaria."
          helpBullets={[
            "Producto: stock, variantes, costo, compras y ventas.",
            "Servicio: duración, modalidad, agenda y reservas.",
            "Insumos, gastos y activos pueden mantenerse sólo para uso interno.",
          ]}
          compact
        />
        {(menuView || canCreateItems) && !recipesView ? (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ alignSelf: { xs: "stretch", md: "auto" } }}
          >
            {menuView ? (
              <Button
                variant="outlined"
                disabled={loading || !productsLoaded}
                onClick={() => { setMenuOrderError(""); setMenuOrderOpen(true); }}
                sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 900, px: 2.2, py: 1.15, color: BRAND.dark, borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.24)" }}
              >
                Ordenar Carta
              </Button>
            ) : null}
            {canCreateItems ? (
              <>
                {!trialNeedsFirstPublication ? (
                  <Button
                    startIcon={trialLimitReached ? <CreditCardRoundedIcon /> : <AddRoundedIcon />}
                    variant="contained"
                    disabled={loading || !productsLoaded}
                    onClick={handleOpenCreate}
                    sx={{
                      minHeight: { xs: 50, sm: 44 },
                      bgcolor: BRAND.orange,
                      borderRadius: 2.5,
                      textTransform: "none",
                      fontWeight: 950,
                      px: 2.5,
                      py: 1.15,
                      boxShadow: "0 10px 24px rgba(243,90,58,0.2)",
                      ...(trialLimitReached
                        ? {
                            "@keyframes trialUpgradeGlow": {
                              "0%, 100%": { boxShadow: "0 0 0 0 rgba(243,90,58,.18)" },
                              "50%": { boxShadow: "0 0 0 9px rgba(243,90,58,0)" },
                            },
                            animation: "trialUpgradeGlow 2s ease-in-out infinite",
                          }
                        : {}),
                      "&:hover": { bgcolor: "#DB4B2F" },
                    }}
                  >
                    {trialLimitReached ? "Agregar más con un plan" : menuView ? "Nuevo plato / producto" : recipesView ? "Nuevo ingrediente / insumo" : "Nuevo ítem"}
                  </Button>
                ) : null}
                <Button
                  startIcon={<MoreHorizRoundedIcon />}
                  endIcon={<ExpandMoreRoundedIcon sx={{ transform: toolsOpen ? "rotate(180deg)" : "none", transition: "transform .18s ease" }} />}
                  variant="outlined"
                  disabled={loading || !productsLoaded}
                  onClick={() => setToolsOpen((current) => !current)}
                  aria-expanded={toolsOpen}
                  sx={{
                    minHeight: { xs: 46, sm: 44 },
                    borderRadius: 2.5,
                    textTransform: "none",
                    fontWeight: 900,
                    px: 2.2,
                    color: BRAND.dark,
                    borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.20)",
                  }}
                >
                  Más opciones
                </Button>
              </>
            ) : null}
          </Stack>
        ) : null}
      </Stack>

      {!recipesView && canCreateItems ? (
        <Collapse in={toolsOpen} timeout="auto" unmountOnExit>
          <Paper variant="outlined" sx={{ p: { xs: 1.25, sm: 1.5 }, borderRadius: 3, bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.018)" }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} useFlexGap flexWrap="wrap">
              <Button
                startIcon={<UploadRoundedIcon />}
                variant="outlined"
                onClick={() => { setError(""); setImportOpen(true); }}
                sx={{ minHeight: 44, textTransform: "none", fontWeight: 900 }}
              >
                Importar ítems
              </Button>
              <Button
                startIcon={<DownloadRoundedIcon />}
                variant="outlined"
                onClick={() => handleDownloadCodes(filteredProducts, "webprofit-codigos-items.pdf")}
                disabled={!filteredProducts.length}
                sx={{ minHeight: 44, textTransform: "none", fontWeight: 900 }}
              >
                Descargar códigos
              </Button>
              {canUpdateItems ? (
                <Button
                  startIcon={<TrendingUpRoundedIcon />}
                  variant="outlined"
                  onClick={openBulkPriceAdjustment}
                  disabled={!filteredProducts.length}
                  sx={{ minHeight: 44, textTransform: "none", fontWeight: 900 }}
                >
                  Ajuste masivo de precios
                </Button>
              ) : null}
            </Stack>
          </Paper>
        </Collapse>
      ) : null}

      {error && !open ? <Alert severity="error">{error}</Alert> : null}
      {menuOrderNotice ? <Alert severity="success" onClose={() => setMenuOrderNotice("")} sx={{ borderRadius: 3 }}>{menuOrderNotice}</Alert> : null}

      {menuView ? (
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          La carta usa los mismos ítems del negocio. Desde acá gestionás disponibilidad, sección y presentación gastronómica.
        </Alert>
      ) : null}

      {recipesView ? (
        <RecipesWorkspace
          projectId={projectId}
          products={products}
          loadingProducts={loading}
          canCreateItems={canCreateItems}
          canUpdateItems={canUpdateItems}
          recipes={recipeSummaries}
          recipesLoading={recipesLoading}
          onRefreshRecipes={refreshRecipeSummaries}
          onCreateSupply={() => openCreateWithType("supply")}
          onManageSupplies={manageRecipeSupplies}
          initialProductId={searchParams.get("producto") || ""}
          onClearInitialProduct={clearRecipeSelection}
        />
      ) : (
        <>
      {canCreateItems && trialNeedsFirstPublication && !loading ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3.5,
            border: "1px solid rgba(243,90,58,.24)",
            bgcolor: "rgba(243,90,58,.045)",
            px: { xs: 1.8, md: 2.2 },
            py: 1.6,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ md: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  flex: "0 0 auto",
                  borderRadius: 2.3,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "rgba(243,90,58,.12)",
                  color: BRAND.orange,
                }}
              >
                <AutoAwesomeRoundedIcon />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: BRAND.orange, fontWeight: 950, letterSpacing: .55 }}>
                  MISIÓN DEL TRIAL
                </Typography>
                <Typography sx={{ color: BRAND.dark, fontWeight: 950, lineHeight: 1.2 }}>
                  Cargá tu primera publicación gratis
                </Typography>
                <Typography variant="caption" sx={{ display: "block", mt: .25, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
                  Elegí producto o servicio. Durante la prueba podés cargar hasta {trialItemsLimit} publicaciones.
                </Typography>
              </Box>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Button
                variant="contained"
                startIcon={<Inventory2RoundedIcon />}
                onClick={() => openCreateWithType("product")}
                sx={{
                  bgcolor: BRAND.orange,
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 950,
                  px: 2.2,
                  "@keyframes firstPublicationGlow": {
                    "0%, 100%": { boxShadow: "0 0 0 0 rgba(243,90,58,.22)" },
                    "50%": { boxShadow: "0 0 0 9px rgba(243,90,58,0)" },
                  },
                  animation: "firstPublicationGlow 1.8s ease-in-out infinite",
                  "&:hover": { bgcolor: "#DB4B2F" },
                }}
              >
                Cargar producto
              </Button>
              <Button
                variant="outlined"
                startIcon={<DesignServicesRoundedIcon />}
                onClick={() => openCreateWithType("service")}
                sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900, color: BRAND.dark, borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.22)" }}
              >
                Cargar servicio
              </Button>
            </Stack>
          </Stack>
        </Paper>
      ) : null}

      {isTrial && productsLoaded && !loading && products.length >= 1 ? (
        <Alert
          severity={trialLimitReached ? "warning" : "success"}
          icon={<RocketLaunchRoundedIcon fontSize="inherit" />}
          sx={{
            borderRadius: 3,
            "& .MuiAlert-message": { width: "100%" },
          }}
        >
          <Typography variant="body2" sx={{ color: BRAND.dark, fontWeight: 900 }}>
            Trial: {trialItemsUsed}/{trialItemsLimit} publicaciones cargadas. {trialLimitReached
              ? "Alcanzaste el máximo; podés editar o borrar una para liberar lugar."
              : `Todavía podés cargar ${trialItemsRemaining} más.`}
          </Typography>
        </Alert>
      ) : null}

      {!compactLayout ? inventorySummaryCards : null}

      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),0.09)",
          overflow: "hidden",
          bgcolor: "var(--wp-panel-surface, #fff)",
          boxShadow: "0 18px 50px rgba(var(--wp-panel-shadow-rgb, 25,36,47),0.06)",
        }}
      >
        <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
          <ResponsiveListControls
            search={(
              <TextField
                value={searchTerm}
                onChange={(event) => { setSearchTerm(event.target.value); setScanNotice(""); }}
                placeholder="Buscar por nombre, código, categoría o etiqueta"
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Escanear código">
                        <IconButton
                          size="small"
                          onClick={() => setScannerOpen(true)}
                          aria-label="Escanear código para buscar un ítem"
                          sx={{ width: 40, height: 40, color: BRAND.dark }}
                        >
                          <QrCodeScannerRoundedIcon />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                sx={{ "& .MuiOutlinedInput-root": { minHeight: 46, borderRadius: 2.5, bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.025)" } }}
              />
            )}
            activeFilterCount={[
              typeFilter !== "all",
              statusFilter !== "all",
              categoryFilter !== "all",
              subcategoryFilter !== "all",
              supplierFilter !== "all",
            ].filter(Boolean).length}
            onClear={clearFilters}
            filterLabel="Filtros"
            drawerTitle="Filtrar ítems"
            actions={(
              <Tooltip title="Actualizar listado">
                <span>
                  <IconButton
                    onClick={loadProducts}
                    disabled={loading}
                    aria-label="Actualizar ítems"
                    sx={{ width: 44, height: 44, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.14)", borderRadius: 2.5, color: BRAND.dark }}
                  >
                    <RefreshRoundedIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          >
            <TextField select size="small" label="Tipo" value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)} sx={{ minWidth: { md: 145 } }}>
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="product">Productos</MenuItem>
              <MenuItem value="service">Servicios</MenuItem>
              <MenuItem value="supply">Insumos</MenuItem>
              <MenuItem value="expense">Gastos</MenuItem>
              <MenuItem value="asset">Activos</MenuItem>
              <MenuItem value="charge">Cargos</MenuItem>
            </TextField>
            <TextField select size="small" label="Estado" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} sx={{ minWidth: { md: 140 } }}>
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="active">Activos</MenuItem>
              <MenuItem value="inactive">Ocultos</MenuItem>
            </TextField>
            <TextField select size="small" label="Categoría" value={categoryFilter} onChange={(event) => { setCategoryFilter(event.target.value); setSubcategoryFilter("all"); }} sx={{ minWidth: { md: 175 } }}>
              <MenuItem value="all">Todas</MenuItem>
              {categories.map((category) => <MenuItem key={category} value={category}>{category}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Subcategoría" value={subcategoryFilter} onChange={(event) => setSubcategoryFilter(event.target.value)} disabled={categoryFilter === "all" || !subcategories.length} sx={{ minWidth: { md: 175 } }}>
              <MenuItem value="all">Todas</MenuItem>
              {subcategories.map((subcategory) => <MenuItem key={subcategory} value={subcategory}>{subcategory}</MenuItem>)}
            </TextField>
            {supplierOptions.length ? (
              <TextField select size="small" label="Proveedor" value={supplierFilter} onChange={(event) => setSupplierFilter(event.target.value)} sx={{ minWidth: { md: 175 } }}>
                <MenuItem value="all">Todos</MenuItem>
                {supplierOptions.map((supplierId) => <MenuItem key={supplierId} value={supplierId}>{`Proveedor · ${supplierId.slice(-8)}`}</MenuItem>)}
              </TextField>
            ) : null}
          </ResponsiveListControls>
        </Box>

        <Divider />
        {loading ? <WebProfitSpinner size={24} sx={{ mx: "auto", my: 1 }} /> : null}
        {scanNotice ? (
          <Alert severity="success" onClose={() => setScanNotice("")} sx={{ mx: { xs: 2, md: 2.5 }, mt: 1.5 }}>
            {scanNotice}
          </Alert>
        ) : null}

        <Box
          sx={{
            px: { xs: 2, md: 2.5 },
            py: 1.5,
            bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.018)",
            borderBottom: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),0.07)",
          }}
        >
          <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
            {filteredProducts.length} resultado
            {filteredProducts.length === 1 ? "" : "s"}
            {hasActiveFilters ? ` de ${products.length}` : ""}
          </Typography>
        </Box>

        <TableContainer sx={{ minHeight: { xs: 0, md: 360 }, p: { xs: 1.1, md: 0 }, maxWidth: "100%", overflowX: "auto" }}>
          <ResponsiveOperationalTable
            stickyHeader
            sx={{
              minWidth: 1120,
              "& .wp-items-actions-cell": {
                position: "sticky",
                right: 0,
                zIndex: 3,
                minWidth: 132,
                bgcolor: "var(--wp-panel-surface, #fff)",
                boxShadow: "-8px 0 14px rgba(0,0,0,.07)",
              },
              "& thead .wp-items-actions-cell": {
                zIndex: 6,
                bgcolor: "var(--wp-panel-surface-soft, #F7F8FB)",
              },
            }}
            aria-label="Listado de ítems"
            mobileLabel="Ítems del negocio"
            collapseAfter={4}
            mobileEssentialLabels={["Publicación", "Precio", "Stock / Duración", "Estado"]}
          >
            <TableHead>
              <TableRow>
                {[
                  "Publicación",
                  "Tipo",
                  "SKU / Código",
                  "Categoría",
                  "Precio",
                  "Stock / Duración",
                  "Estado",
                  "Acciones",
                ].map((label) => (
                  <TableCell
                    key={label}
                    align={label === "Acciones" ? "right" : "left"}
                    className={label === "Acciones" ? "wp-items-actions-cell" : undefined}
                    sx={{
                      bgcolor: "var(--wp-panel-surface-soft, #F7F8FB)",
                      color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)",
                      fontSize: 12,
                      fontWeight: 950,
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                      borderBottom: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),0.08)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {!loading && paginatedProducts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ py: 9, borderBottom: 0 }}>
                    <Stack alignItems="center" spacing={1.5}>
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: 3,
                          bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.05)",
                          display: "grid",
                          placeItems: "center",
                        }}
                      >
                        <Inventory2RoundedIcon
                          sx={{ fontSize: 34, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}
                        />
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 950, color: BRAND.dark }}
                      >
                        {menuView && filteredProducts.length === 0
                          ? "Todavía no hay ítems en la carta"
                          : products.length
                            ? "No encontramos coincidencias"
                            : isTrial
                              ? "Tu catálogo está listo para empezar"
                              : "Todavía no hay publicaciones"}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)",
                          textAlign: "center",
                        }}
                      >
                        {menuView && filteredProducts.length === 0
                          ? "Creá un ítem desde esta vista o editá uno existente para incluirlo en la carta gastronómica."
                          : products.length
                            ? "Probá cambiando la búsqueda o quitando algún filtro."
                            : isTrial
                              ? "Elegí qué vendés y te llevamos directo al formulario correcto."
                              : "Creá el primer producto o servicio para comenzar el inventario."}
                      </Typography>
                      {products.length ? (
                        <Button
                          onClick={clearFilters}
                          sx={{ textTransform: "none", fontWeight: 850 }}
                        >
                          Limpiar filtros
                        </Button>
                      ) : isTrial && canCreateItems ? (
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1 }}>
                          <Button
                            onClick={() => openCreateWithType("product")}
                            variant="contained"
                            startIcon={<Inventory2RoundedIcon />}
                            sx={{
                              bgcolor: BRAND.orange,
                              textTransform: "none",
                              fontWeight: 900,
                              borderRadius: 2.5,
                            }}
                          >
                            Cargar producto
                          </Button>
                          <Button
                            onClick={() => openCreateWithType("service")}
                            variant="outlined"
                            startIcon={<DesignServicesRoundedIcon />}
                            sx={{
                              textTransform: "none",
                              fontWeight: 900,
                              borderRadius: 2.5,
                            }}
                          >
                            Cargar servicio
                          </Button>
                        </Stack>
                      ) : canCreateItems ? (
                        <Button
                          onClick={handleOpenCreate}
                          variant="contained"
                          startIcon={<AddRoundedIcon />}
                          sx={{
                            mt: 1,
                            bgcolor: BRAND.orange,
                            textTransform: "none",
                            fontWeight: 900,
                            borderRadius: 2.5,
                          }}
                        >
                          Crear publicación
                        </Button>
                      ) : null}
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : null}

              {paginatedProducts.map((product) => {
                const itemKind = normalizeItemKind(product);
                const isService = itemKind === "service";
                const capabilities = productCapabilities(product);
                const isActive = productIsActive(product);
                const imageUrl = getProductImageUrls(product)[0];
                const stock = productStock(product);
                const displayName = productName(product);
                const description = productDescription(product);
                const code = productCode(product);
                const unit = product.unit || product.unidad || "";
                const recipeSummary = recipeSummaryByProduct.get(String(productId(product))) || null;

                return (
                  <TableRow
                    key={productId(product)}
                    hover
                    sx={{
                      "&:last-child td": { borderBottom: 0 },
                      "&:hover": { bgcolor: "rgba(243,90,58,0.025)" },
                    }}
                  >
                    <TableCell sx={{ minWidth: 320, py: 1.6 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          variant="rounded"
                          src={imageUrl || undefined}
                          alt={displayName}
                          sx={{
                            width: 52,
                            height: 52,
                            borderRadius: 2,
                            bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.06)",
                            border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),0.08)",
                            color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)",
                          }}
                        >
                          {isService ? (
                            <DesignServicesRoundedIcon />
                          ) : (
                            <ImageRoundedIcon />
                          )}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              color: BRAND.dark,
                              fontWeight: 900,
                              lineHeight: 1.25,
                            }}
                          >
                            {displayName}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)",
                              mt: 0.45,
                              maxWidth: 360,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {description || "Sin descripción"}
                          </Typography>
                          {product.hasVariants || product.variants?.length ? (
                            <Chip
                              size="small"
                              label={`${(product.variants || product.variations || []).length} variantes`}
                              sx={{ mt: 0.8, height: 22, fontWeight: 850 }}
                            />
                          ) : null}
                          {gastronomicMenuEnabled && product.gastronomicMenu?.enabled === true ? (
                            <>
                              <Chip
                                size="small"
                                label={`Carta · ${product.gastronomicMenu?.section || productCategory(product) || "General"}`}
                                sx={{
                                  mt: 0.8,
                                  ml: product.hasVariants || product.variants?.length ? 0.8 : 0,
                                  height: 22,
                                  fontWeight: 850,
                                  bgcolor: "rgba(243,90,58,.08)",
                                  color: BRAND.orange,
                                }}
                              />
                              {product.gastronomicMenu?.available === false ? (
                                <Chip
                                  size="small"
                                  label="No disponible en carta"
                                  color="warning"
                                  variant="outlined"
                                  sx={{ mt: 0.8, ml: 0.8, height: 22, fontWeight: 850 }}
                                />
                              ) : null}
                            </>
                          ) : null}
                          {recipesEnabled && product.gastronomicMenu?.enabled === true && (recipeSummary || canUpdateItems) ? (
                            <Chip
                              size="small"
                              label={recipeSummary ? `Receta · ${recipeSummary.ingredientCount}` : "Sin receta"}
                              color={recipeSummary?.requiresReview ? "warning" : recipeSummary ? "success" : "default"}
                              variant={recipeSummary ? "filled" : "outlined"}
                              sx={{ mt: 0.8, ml: 0.8, height: 22, fontWeight: 850 }}
                            />
                          ) : null}
                        </Box>
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        label={ITEM_KIND_LABELS[itemKind] || itemKind}
                        variant="outlined"
                        sx={{
                          fontWeight: 800,
                          borderColor: capabilities.publicVisible
                            ? "rgba(25,118,210,0.28)"
                            : "rgba(123,91,181,0.3)",
                          color: capabilities.publicVisible ? "#1565C0" : "#6B4CA5",
                          bgcolor: capabilities.publicVisible
                            ? "rgba(25,118,210,0.04)"
                            : "rgba(123,91,181,0.05)",
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: code ? "monospace" : "inherit",
                          color: code ? BRAND.dark : "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.38)",
                          fontWeight: code ? 750 : 500,
                        }}
                      >
                        {code || "Sin código"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ color: BRAND.dark }}>
                        {productCategory(product)}
                      </Typography>
                      {productSubcategory(product) ? (
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}
                        >
                          {productSubcategory(product)}
                        </Typography>
                      ) : null}
                    </TableCell>

                    <TableCell>
                      <Typography sx={{ color: BRAND.dark, fontWeight: 900 }}>
                        {priceLabel(product)}
                      </Typography>
                      {unit ? (
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}
                        >
                          por {unit}
                        </Typography>
                      ) : null}
                    </TableCell>

                    <TableCell>
                      {isService ? (
                        <Box>
                          <Typography variant="body2" sx={{ color: BRAND.dark, fontWeight: 850 }}>
                            {durationLabel(product.durationMinutes || product.duracionMinutos)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
                            {product.modality || product.modalidad || "Modalidad no indicada"}
                          </Typography>
                        </Box>
                      ) : capabilities.stockTracked ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography
                            sx={{
                              color: stock <= Number(product.inventory?.reorderPoint ?? 5) ? "#C4452D" : BRAND.dark,
                              fontWeight: 950,
                            }}
                          >
                            {stock}
                          </Typography>
                          {stock <= Number(product.inventory?.reorderPoint ?? 5) ? (
                            <Chip
                              size="small"
                              label={stock === 0 ? "Sin stock" : "Reponer"}
                              sx={{
                                height: 22,
                                fontSize: 11,
                                fontWeight: 850,
                                color: "#B23D28",
                                bgcolor: "rgba(243,90,58,0.09)",
                              }}
                            />
                          ) : null}
                        </Stack>
                      ) : (
                        <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
                          Sin control de stock
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        label={isActive ? "Activo" : "Oculto"}
                        sx={{
                          fontWeight: 850,
                          color: isActive ? "#237A45" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.58)",
                          bgcolor: isActive
                            ? "rgba(46,160,83,0.1)"
                            : "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.07)",
                        }}
                      />
                    </TableCell>

                    <TableCell align="right" className="wp-items-actions-cell">
                      <Stack direction="row" spacing={0.75} justifyContent="flex-end" useFlexGap flexWrap="nowrap">
                        {canUpdateItems ? (
                          <>
                            <Button
                              variant="contained"
                              startIcon={<EditRoundedIcon />}
                              onClick={() => handleOpenEdit(product)}
                              sx={{ display: { xs: "inline-flex", md: "none" }, minHeight: 44, textTransform: "none", fontWeight: 900 }}
                            >
                              Editar
                            </Button>
                            <Tooltip title="Editar ítem">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEdit(product)}
                                aria-label={`Editar ${displayName}`}
                                sx={{ display: { xs: "none", md: "inline-flex" }, color: BRAND.dark }}
                              >
                                <EditRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : null}
                        {recipesEnabled && product.gastronomicMenu?.enabled === true ? (
                          <>
                            <Button
                              variant="outlined"
                              startIcon={<RestaurantMenuRoundedIcon />}
                              onClick={() => openRecipeForProduct(product)}
                              sx={{ display: { xs: "inline-flex", md: "none" }, minHeight: 44, textTransform: "none", fontWeight: 850 }}
                            >
                              {canUpdateItems ? (recipeSummary ? "Editar receta" : "Crear receta") : "Ver receta"}
                            </Button>
                            <Tooltip title={canUpdateItems ? (recipeSummary ? "Editar receta" : "Crear receta") : "Ver receta"}>
                              <IconButton
                                size="small"
                                onClick={() => openRecipeForProduct(product)}
                                aria-label={`${canUpdateItems ? (recipeSummary ? "Editar" : "Crear") : "Ver"} receta de ${displayName}`}
                                sx={{ display: { xs: "none", md: "inline-flex" }, color: recipeSummary ? "success.main" : BRAND.orange }}
                              >
                                <RestaurantMenuRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : null}
                        {canDeleteItems ? (
                          <Tooltip title="Eliminar ítem">
                            <IconButton
                              onClick={() => handleDelete(product)}
                              aria-label={`Eliminar ${displayName}`}
                              sx={{ minWidth: 44, minHeight: 44, color: BRAND.orange, border: { xs: "1px solid rgba(243,90,58,.24)", md: "none" }, borderRadius: 2.5 }}
                            >
                              <DeleteRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </ResponsiveOperationalTable>
        </TableContainer>

        <Divider />
        <TablePagination
          component="div"
          count={filteredProducts.length}
          page={page}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage={compactLayout ? "Por página" : "Filas por página"}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
          sx={{
            color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.68)",
            px: { xs: 0.5, sm: 1.5 },
            ".MuiTablePagination-toolbar": { flexWrap: "wrap", justifyContent: "flex-end", minHeight: 56 },
            ".MuiTablePagination-spacer": { display: { xs: "none", sm: "block" } },
            ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
              {
                fontWeight: 700,
              },
          }}
        />
      </Paper>
      {compactLayout ? (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)", fontWeight: 900 }}>Resumen</Typography>
          {inventorySummaryCards}
        </Box>
      ) : null}
        </>
      )}

      <ProductImportWizard
        open={importOpen}
        projectId={projectId}
        onImported={loadProducts}
        onClose={() => setImportOpen(false)}
      />

      <Dialog
        open={bulkPriceOpen}
        onClose={bulkPriceSaving ? undefined : () => setBulkPriceOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 950, color: BRAND.dark }}>Ajuste masivo de precios</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">
              Se aplicará sobre <strong>{filteredProducts.length}</strong> ítem(s) del filtro actual.
              También se ajustan precios explícitos de variantes, escalas por cantidad y referencia de membresía.
            </Alert>
            <TextField
              autoFocus
              label="Porcentaje"
              type="number"
              value={bulkPricePercent}
              onChange={(event) => {
                setBulkPricePercent(event.target.value);
                setBulkPricePreview(null);
                setBulkPriceError("");
              }}
              inputProps={{ step: "0.01", min: "-99.99" }}
              helperText="Ej.: 12,5 para aumentar; -8 para reducir."
              fullWidth
            />
            {bulkPriceError ? <Alert severity="error">{bulkPriceError}</Alert> : null}
            {bulkPricePreview ? (
              <Box sx={{ p: 2, borderRadius: 2.5, bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.04)" }}>
                <Typography sx={{ fontWeight: 900, mb: 1 }}>
                  Vista previa · {bulkPricePreview.matched || 0} ítem(s)
                </Typography>
                <Stack spacing={.7}>
                  {(bulkPricePreview.sample || []).map((item) => (
                    <Stack key={item.id} direction="row" justifyContent="space-between" spacing={2}>
                      <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>{item.name}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 900, whiteSpace: "nowrap" }}>
                        {Number(item.previousPrice || 0).toLocaleString("es-AR")} → {Number(item.newPrice || 0).toLocaleString("es-AR")}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button onClick={() => setBulkPriceOpen(false)} disabled={bulkPriceSaving}>Cancelar</Button>
          <Button variant="outlined" onClick={previewBulkPriceAdjustment} disabled={bulkPriceSaving}>
            Vista previa
          </Button>
          <Button
            variant="contained"
            onClick={applyBulkPriceAdjustment}
            disabled={bulkPriceSaving || !bulkPricePreview?.matched}
            sx={{ bgcolor: BRAND.dark, "&:hover": { bgcolor: "#101820" } }}
          >
            Aplicar ajuste
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        fullScreen={mobile}
        maxWidth="md"
        PaperProps={{
          sx: {
            display: "flex",
            flexDirection: "column",
            minHeight: mobile ? "100dvh" : "min(780px, calc(100dvh - 48px))",
            maxHeight: mobile ? "100dvh" : "calc(100dvh - 48px)",
            borderRadius: mobile ? 0 : 4,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            position: "relative",
            px: { xs: 2, sm: 3 },
            py: { xs: 1.8, sm: 2.3 },
            pr: 7,
            color: "#fff",
            background: "linear-gradient(135deg,#19242F 0%,#30495B 74%,#3B5A70 100%)",
            overflow: "hidden",
            "&::after": {
              content: '""',
              position: "absolute",
              width: 145,
              height: 145,
              right: -55,
              bottom: -95,
              borderRadius: "50%",
              bgcolor: "rgba(243,90,58,.27)",
            },
          }}
        >
          <Typography variant="overline" sx={{ fontWeight: 950, letterSpacing: ".12em", color: "#FFAA96" }}>
            {gastronomicMenuEnabled ? "CARTA E ÍTEMS" : "CATÁLOGO Y OPERACIÓN"}
          </Typography>
          <Typography variant={mobile ? "h6" : "h5"} sx={{ fontWeight: 950, lineHeight: 1.15 }}>
            {editingProduct
              ? guidedGastronomicForm
                ? `Editar ${form.erpItemKind === "supply" ? "ingrediente / insumo" : "plato / producto"}`
                : `Editar ${form.erpItemKind === "service" ? "servicio" : "ítem"}`
              : guidedGastronomicForm
                ? `Nuevo ${form.erpItemKind === "supply" ? "ingrediente / insumo" : "plato / producto"}`
                : `Nuevo ${form.erpItemKind === "service" ? "servicio" : "ítem"}`}
          </Typography>
          {!mobile ? (
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,.72)", mt: 0.5 }}>
              {guidedGastronomicForm
                ? "Completá primero lo esencial. La configuración ERP sigue disponible en Más opciones y las existencias se gestionan desde Movimientos."
                : "Definí qué es, cómo se usa y cómo se presenta. El stock del Sistema se registra después mediante movimientos."}
            </Typography>
          ) : null}
          <IconButton
            onClick={handleClose}
            disabled={saving || processingImages}
            aria-label="Cerrar formulario"
            sx={{ position: "absolute", right: 10, top: 10, color: "#fff", zIndex: 1 }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}
        >
          <DialogContent
            ref={formContentRef}
            dividers
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overscrollBehavior: "contain",
              p: mobile ? 1.35 : 3,
              bgcolor: "var(--wp-panel-surface-soft, #F7F8FB)",
              scrollbarWidth: "thin",
              scrollbarColor: `${BRAND.orange} rgba(var(--wp-panel-ink-rgb, 25,36,47),.10)`,
              "&::-webkit-scrollbar": { width: 10 },
              "&::-webkit-scrollbar-track": { bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.07)" },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: BRAND.orange,
                borderRadius: 999,
                border: "2px solid var(--wp-panel-surface-soft, #F6F8FA)",
              },
            }}
          >
            <Stack spacing={2.4}>
              {formFeedback ? (
                <Alert
                  severity={formFeedback.kind === "success" ? "success" : ["validation", "warning"].includes(formFeedback.kind) ? "warning" : "error"}
                  sx={{
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor:
                      formFeedback.kind === "success"
                        ? "rgba(46,125,50,.28)"
                        : ["validation", "warning"].includes(formFeedback.kind)
                          ? "rgba(237,108,2,.28)"
                          : "rgba(211,47,47,.28)",
                    alignItems: "flex-start",
                  }}
                >
                  <AlertTitle sx={{ fontWeight: 950 }}>{formFeedback.title}</AlertTitle>
                  <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                    {formFeedback.message}
                  </Typography>
                  {formFeedback.detail ? (
                    <Typography variant="body2" sx={{ mt: 0.7, opacity: 0.82 }}>
                      {formFeedback.detail}
                    </Typography>
                  ) : null}
                  {formFeedback.requestId || (formFeedback.code && formFeedback.code !== "INTERNAL_ERROR") ? (
                    <Typography variant="caption" component="div" sx={{ mt: 0.85, fontWeight: 800, opacity: 0.72 }}>
                      {formFeedback.requestId ? `Solicitud: ${formFeedback.requestId}` : ""}
                      {formFeedback.requestId && formFeedback.code && formFeedback.code !== "INTERNAL_ERROR" ? " · " : ""}
                      {formFeedback.code && formFeedback.code !== "INTERNAL_ERROR" ? `Código: ${formFeedback.code}` : ""}
                    </Typography>
                  ) : null}
                </Alert>
              ) : error ? (
                <Alert severity="error">{error}</Alert>
              ) : null}

              {gastronomicMenuEnabled ? (
                <GastronomicItemWizard
                  form={form}
                  setForm={setForm}
                  fieldErrors={fieldErrors}
                  step={gastroStep}
                  setStep={setGastroStep}
                  editing={Boolean(editingProduct)}
                  menuView={menuView}
                  recipesView={recipesView}
                  sectionOptions={gastronomicSections}
                  subcategoryOptions={formSubcategories}
                  effectiveSection={effectiveGastronomicSection}
                  categoryLinked={gastroCategoryLinked}
                  onSectionChange={handleGastronomicSectionChange}
                  onCategoryChange={handleGastronomicCategoryChange}
                  onRoleChange={handleGastronomicRoleChange}
                  imageEditor={(
                    <ItemImagesEditor
                      existingImages={existingImages}
                      selectedFiles={selectedFiles}
                      previewUrls={previewUrls}
                      processingImages={processingImages}
                      saving={saving}
                      imagePreparationProgress={imagePreparationProgress}
                      imageUploadProgress={imageUploadProgress}
                      imageError={imageError}
                      maxImages={MAX_IMAGES}
                      mobile={mobile}
                      onRemoveExisting={handleRemoveExistingImage}
                      onRemoveSelected={handleRemoveSelectedFile}
                      onFilesChange={handleFilesChange}
                    />
                  )}
                  variantsEditor={(
                    <ProductVariantsEditor
                      form={form}
                      setForm={setForm}
                      imageOptions={variantImageOptions(existingImages, previewUrls)}
                      compact={compactLayout}
                      gastronomic
                    />
                  )}
                  positionEditor={(
                    <GastronomicSectionPositionEditor
                      products={products}
                      currentProductId={productId(editingProduct)}
                      currentName={form.name || "Este producto"}
                      sectionName={effectiveGastronomicSection}
                      sortOrder={form.gastronomicSortOrder}
                      onChange={(value) => setForm((current) => ({ ...current, gastronomicSortOrder: value }))}
                      onOpenFullOrder={() => setMenuOrderOpen(true)}
                    />
                  )}
                  quantityPricingEditor={(
                    <QuantityDiscountsEditor form={form} setForm={setForm} compact={compactLayout} gastronomic />
                  )}
                  promotionsEditor={(
                    <GastronomicPromotionsEditor
                      drafts={promotionDrafts}
                      setDrafts={setPromotionDrafts}
                      products={products.filter((item) => normalizeItemKind(item) === "product")}
                      currentProductId={productId(editingProduct)}
                      currentProduct={editingProduct || { name: form.name, variants: form.variants }}
                      canManage={canUpdateItems}
                      loading={promotionLoading}
                      error={promotionError}
                    />
                  )}
                  currentStock={form.stock}
                  canManageStock={canManageStock}
                  stockMovementUrl={productId(editingProduct) ? `/dashboard/proyectos/${projectId}/sistema/movimientos?nuevo=1&tipo=stock_adjustment&itemId=${productId(editingProduct)}` : ""}
                  createdSupplyId={createdSupplyId}
                  advancedEditor={(
                    <GastronomicAdvancedEditor
                      form={form}
                      setForm={setForm}
                      categories={categories}
                      subcategories={formSubcategories}
                      onCategoryChange={handleGastronomicCategoryChange}
                      canManageCosts={canManageCosts}
                      canManageAccounting={canManageAccounting}
                      compact={compactLayout}
                      fieldErrors={fieldErrors}
                      editing={Boolean(editingProduct)}
                      onOpenScanner={() => setCreateScannerOpen(true)}
                      onDownloadCode={handleDownloadCurrentFormCode}
                      canDownloadCode={Boolean(String(form.code || "").trim() || (form.variants || []).some((variant) => variant?.barcode || variant?.sku))}
                    />
                  )}
                  hasAdvancedValues={Boolean(
                    String(form.code || "").trim()
                    || String(form.tags || "").trim()
                    || (form.attributes || []).length
                    || (form.erpItemKind === "product" && String(form.unit || "").trim())
                    || Number(form.lastCost || form.averageCost || form.standardCost || 0) > 0
                    || Number(form.minimumStock || form.reorderPoint || 0) > 0
                    || String(form.inventoryLocation || "").trim()
                    || String(form.taxProfileCode || "").trim()
                  )}
                />
              ) : null}

              <Box sx={{ display: guidedGastronomicForm ? "none" : "contents" }}>
              <Paper
                elevation={0}
                sx={{
                  display: { xs: "none", sm: "block" },
                  p: { xs: 1.25, sm: 1.6 },
                  borderRadius: 3,
                  border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.09)",
                  bgcolor: "var(--wp-panel-surface, #fff)",
                }}
              >
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  {[
                    ["1", "Crear el ítem", "Nombre, tipo y precio"],
                    ["2", "Definir su uso", "Venta, compra, publicación y operación"],
                    ["3", "Publicar", "Guardar el ítem sin alterar existencias"],
                  ].map(([number, label, detail]) => (
                    <Box
                      key={number}
                      sx={{
                        flex: 1,
                        p: 1.05,
                        borderRadius: 2.2,
                        bgcolor: number === "1" ? "rgba(243,90,58,.08)" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.035)",
                        border: number === "1" ? "1px solid rgba(243,90,58,.22)" : "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.06)",
                      }}
                    >
                      <Stack direction="row" spacing={0.9} alignItems="center">
                        <Chip
                          size="small"
                          label={number}
                          sx={{ minWidth: 28, fontWeight: 950, bgcolor: number === "1" ? BRAND.orange : BRAND.dark, color: number === "1" ? "#17181C" : "#fff" }}
                        />
                        <Box>
                          <Typography variant="caption" sx={{ display: "block", fontWeight: 950, color: BRAND.dark }}>
                            {label}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
                            {detail}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Paper>


              <ItemFormHeading
                step="1"
                title="Información básica"
                description="Elegí la plantilla y cargá los datos que identifican al ítem para tu equipo y para el cliente."
              />

              <TextField
                select
                label="Plantilla de ítem"
                name="erpItemKind"
                value={form.erpItemKind}
                onChange={(event) => {
                  const nextKind = event.target.value;
                  setForm((current) => {
                    const nextCapabilities = capabilitiesForKind(nextKind);
                    const supportsVariants = supportsVariantsForKind(nextKind);
                    const isService = nextKind === "service";

                    return {
                      ...current,
                      erpItemKind: nextKind,
                      itemType: isService ? "service" : "product",
                      ...nextCapabilities,
                      requiresBooking: Boolean(nextCapabilities.reservable),
                      requiresShipping: Boolean(nextCapabilities.shippable),
                      stock: nextCapabilities.stockTracked ? current.stock : "0",
                      durationMinutes: isService ? current.durationMinutes : "",
                      modality: isService ? current.modality : "",
                      bookingConfirmationMode: isService
                        ? current.bookingConfirmationMode
                        : "manual",
                      bookingPaymentMode: isService
                        ? current.bookingPaymentMode
                        : "none",
                      bookingDepositType: isService
                        ? current.bookingDepositType
                        : "fixed",
                      bookingDepositValue: isService
                        ? current.bookingDepositValue
                        : "0",
                      capacity: isService ? current.capacity : "1",
                      bufferBeforeMinutes: isService
                        ? current.bufferBeforeMinutes
                        : "0",
                      bufferAfterMinutes: isService
                        ? current.bufferAfterMinutes
                        : "0",
                      serviceLocation: isService ? current.serviceLocation : "",
                      meetingUrl: isService ? current.meetingUrl : "",
                      bookingInstructions: isService
                        ? current.bookingInstructions
                        : "",
                      cancellationWindowHours: isService
                        ? current.cancellationWindowHours
                        : "0",
                      hasVariants: supportsVariants ? current.hasVariants : false,
                      variantAttributes: supportsVariants
                        ? current.variantAttributes
                        : [],
                      variants: supportsVariants ? current.variants : [],
                    };
                  });
                }}
                helperText={mobile ? "Podés ajustar su uso después." : "La plantilla propone capacidades iniciales, pero podés personalizarlas debajo."}
                fullWidth
              >
                {Object.entries(ITEM_KIND_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </TextField>

              <TextField
                label={
                  form.erpItemKind === "service"
                    ? "Nombre del servicio"
                    : form.erpItemKind === "expense"
                      ? "Nombre del gasto"
                      : gastronomicMenuEnabled
                        ? "Nombre del plato / ítem"
                        : "Nombre del ítem"
                }
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                error={Boolean(fieldErrors.name)}
                helperText={fieldErrors.name || ""}
                fullWidth
              />
              <TextField
                label="Descripción"
                name="description"
                value={form.description}
                onChange={handleChange}
                multiline
                minRows={mobile ? 2 : 4}
                fullWidth
              />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "minmax(0,1fr)", md: "repeat(2,minmax(0,1fr))" },
                  gap: 2,
                }}
              >
                <Autocomplete
                  freeSolo
                  autoHighlight
                  options={categories}
                  value={form.category || null}
                  inputValue={form.category}
                  onChange={(_event, value) => {
                    const nextCategory = String(value || "");
                    setForm((current) => ({
                      ...current,
                      category: nextCategory,
                      subcategory: sameClassification(current.category, nextCategory)
                        ? current.subcategory
                        : "",
                    }));
                  }}
                  onInputChange={(_event, value, reason) => {
                    if (reason === "reset") return;
                    setForm((current) => ({
                      ...current,
                      category: value,
                      subcategory: sameClassification(current.category, value)
                        ? current.subcategory
                        : "",
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Categoría"
                      required
                      error={Boolean(fieldErrors.category)}
                      helperText={fieldErrors.category || (mobile ? "Existente o nueva." : "Elegí una existente o escribí una nueva.")}
                    />
                  )}
                  fullWidth
                />
                <Autocomplete
                  freeSolo
                  autoHighlight
                  options={formSubcategories}
                  value={form.subcategory || null}
                  inputValue={form.subcategory}
                  onChange={(_event, value) =>
                    setForm((current) => ({
                      ...current,
                      subcategory: String(value || ""),
                    }))
                  }
                  onInputChange={(_event, value, reason) => {
                    if (reason === "reset") return;
                    setForm((current) => ({ ...current, subcategory: value }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Subcategoría"
                      placeholder="Opcional"
                      helperText={mobile
                        ? "Opcional."
                        : formSubcategories.length
                          ? `Sugerencias de ${form.category || "esta categoría"}.`
                          : "Opcional. Podés crear una para esta categoría."}
                    />
                  )}
                  fullWidth
                />
                <Stack spacing={0.75}>
                  <TextField
                    label="Código interno / SKU"
                    name="code"
                    value={form.code}
                    onChange={(event) => {
                      handleChange(event);
                      setFormCodeNotice("");
                    }}
                    helperText={editingProduct
                      ? "Código estable del ítem. Editar otros campos no lo regenera ni lo reemplaza."
                      : "Podés escanear la etiqueta que el artículo ya trae. Si queda vacío, WebProfit genera un código interno único al crear."}
                    fullWidth
                  />
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={0.75}>
                    {!editingProduct ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<QrCodeScannerRoundedIcon />}
                        onClick={() => setCreateScannerOpen(true)}
                        sx={{ textTransform: "none", fontWeight: 900, justifyContent: "flex-start" }}
                      >
                        Escanear etiqueta
                      </Button>
                    ) : null}
                    {String(form.code || "").trim() || (form.variants || []).some((variant) => variant?.barcode || variant?.sku) ? (
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<DownloadRoundedIcon />}
                        onClick={handleDownloadCurrentFormCode}
                        sx={{ textTransform: "none", fontWeight: 900, justifyContent: "flex-start" }}
                      >
                        Descargar código
                      </Button>
                    ) : null}
                  </Stack>
                  {formCodeNotice ? (
                    <Typography variant="caption" sx={{ color: "success.main", fontWeight: 800 }}>
                      {formCodeNotice}
                    </Typography>
                  ) : null}
                </Stack>
                <TextField
                  label="Unidad"
                  name="unit"
                  value={form.unit}
                  onChange={handleChange}
                  placeholder={
                    form.erpItemKind === "service"
                      ? "sesión, hora, visita"
                      : "unidad, kg, m², mes"
                  }
                  fullWidth
                />
              </Box>

              {gastronomicMenuEnabled ? (
                <Paper
                  variant="outlined"
                  sx={{ p: 2, borderRadius: 3, bgcolor: "rgba(243,90,58,.025)" }}
                >
                  <Stack spacing={1.7}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.5}
                      alignItems={{ sm: "center" }}
                      justifyContent="space-between"
                    >
                      <Box>
                        <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
                          Carta gastronómica
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Usa este mismo ítem, con su precio, imágenes, variantes y datos generales. Acá solo definís su presentación y disponibilidad dentro de la carta.
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 850 }}>
                          Incluir en carta
                        </Typography>
                        <Switch
                          name="gastronomicEnabled"
                          checked={form.gastronomicEnabled}
                          onChange={handleChange}
                        />
                      </Stack>
                    </Stack>

                    {form.gastronomicEnabled ? (
                      <>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1.5}
                          alignItems={{ sm: "center" }}
                          justifyContent="space-between"
                          sx={{ p: 1.5, borderRadius: 2.5, bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.035)" }}
                        >
                          <Box>
                            <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>
                              Disponible ahora en la carta
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Podés pausarlo temporalmente sin desactivar el ítem, cambiar stock ni sacarlo del catálogo general.
                            </Typography>
                          </Box>
                          <Switch
                            name="gastronomicAvailable"
                            checked={form.gastronomicAvailable}
                            onChange={handleChange}
                          />
                        </Stack>
                        <TextField
                          label="Descripción corta para carta"
                          name="gastronomicShortDescription"
                          value={form.gastronomicShortDescription}
                          onChange={handleChange}
                          inputProps={{ maxLength: 280 }}
                          helperText={`${form.gastronomicShortDescription.length}/280 · Resumen breve para la carta; la descripción general del ítem se conserva aparte.`}
                          multiline
                          minRows={2}
                          fullWidth
                        />
                        <TextField
                          label="Observaciones alimentarias"
                          name="gastronomicDietaryNotes"
                          value={form.gastronomicDietaryNotes}
                          onChange={handleChange}
                          inputProps={{ maxLength: 500 }}
                          placeholder="Ej.: vegetariano, sin TACC, contiene frutos secos"
                          helperText="Solo información útil para elegir el plato. No reemplaza ingredientes, variantes ni atributos generales."
                          multiline
                          minRows={2}
                          fullWidth
                        />
                        <TextField
                          label="Sección de la carta"
                          name="gastronomicSection"
                          value={form.gastronomicSection}
                          onChange={handleChange}
                          placeholder={form.category || "Ej.: Entradas, Principales, Bebidas"}
                          helperText="Si queda vacío, la carta usa la categoría actual del ítem."
                          fullWidth
                        />
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                          <TextField
                            label="Orden de sección"
                            name="gastronomicSectionOrder"
                            type="number"
                            value={form.gastronomicSectionOrder}
                            onChange={handleChange}
                            helperText="Menor número aparece primero."
                            fullWidth
                          />
                          <TextField
                            label="Orden dentro de la sección"
                            name="gastronomicSortOrder"
                            type="number"
                            value={form.gastronomicSortOrder}
                            onChange={handleChange}
                            helperText="No modifica el orden del catálogo."
                            fullWidth
                          />
                        </Stack>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Box>
                            <Typography sx={{ fontWeight: 850, color: BRAND.dark }}>
                              Destacar en la carta
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Marca el ítem para presentaciones gastronómicas destacadas sin cambiar el catálogo.
                            </Typography>
                          </Box>
                          <Switch
                            name="gastronomicFeatured"
                            checked={form.gastronomicFeatured}
                            onChange={handleChange}
                          />
                        </Stack>
                      </>
                    ) : null}
                  </Stack>
                </Paper>
              ) : null}

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  select
                  label="Forma de mostrar el precio"
                  name="priceType"
                  value={form.priceType}
                  onChange={handleChange}
                  fullWidth
                >
                  <MenuItem value="fixed">Precio fijo</MenuItem>
                  <MenuItem value="from">Desde</MenuItem>
                  <MenuItem value="quote">Consultar</MenuItem>
                </TextField>
                <TextField
                  label={
                    form.priceType === "quote"
                      ? "Precio de referencia (opcional)"
                      : "Precio"
                  }
                  name="price"
                  type="number"
                  value={form.price}
                  onChange={handleChange}
                  error={Boolean(fieldErrors.price)}
                  helperText={fieldErrors.price || ""}
                  fullWidth
                  inputProps={{ min: 0, step: "0.01" }}
                />
              </Stack>

              <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 3, bgcolor: advancedFormOpen ? "rgba(var(--wp-panel-ink-rgb, 25,36,47),.025)" : "var(--wp-panel-surface, #fff)" }}>
                <Button
                  type="button"
                  fullWidth
                  onClick={() => setAdvancedFormOpen((current) => !current)}
                  endIcon={<ExpandMoreRoundedIcon sx={{ transform: advancedFormOpen ? "rotate(180deg)" : "none", transition: "transform .18s ease" }} />}
                  sx={{ minHeight: 46, justifyContent: "space-between", textTransform: "none", fontWeight: 950, color: BRAND.dark }}
                >
                  {advancedFormOpen ? "Ocultar opciones avanzadas" : "Más opciones"}
                </Button>
                {!advancedFormOpen && editingProduct ? (
                  <Typography variant="caption" sx={{ display: "block", px: 1, pb: .5, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
                    Las configuraciones existentes se conservan aunque esta sección esté cerrada.
                  </Typography>
                ) : null}
              </Paper>

              <Collapse in={advancedFormOpen} timeout="auto" unmountOnExit={false}>
                <Stack spacing={2.4} sx={{ pt: .25 }}>

              <QuantityDiscountsEditor form={form} setForm={setForm} compact={compactLayout} />

              {form.erpItemKind === "service" || supportsVariantsForKind(form.erpItemKind) ? (
                <>
                  <Divider />
                  <ItemFormHeading
                    step="2"
                    title={form.erpItemKind === "service" ? "Cómo se presta el servicio" : "Variantes y presentación comercial"}
                    description={form.erpItemKind === "service"
                      ? "Completá duración, modalidad y turnos solamente cuando correspondan."
                      : "Configurá variantes sólo si el mismo ítem cambia por talle, color, modelo u otra opción."}
                  />

              {form.erpItemKind === "service" ? (
                <Paper
                  variant="outlined"
                  sx={{ p: 2, borderRadius: 3, bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.02)" }}
                >
                  <Stack spacing={2}>
                    <Box>
                      <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
                        Datos del servicio
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Completá duración y modalidad. Al activar turnos, el cliente verá “Ver horarios y reservar” en la web.
                      </Typography>
                    </Box>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        label="Duración estimada (minutos)"
                        name="durationMinutes"
                        type="number"
                        value={form.durationMinutes}
                        onChange={handleChange}
                        error={Boolean(fieldErrors.durationMinutes)}
                        helperText={fieldErrors.durationMinutes || ""}
                        fullWidth
                        inputProps={{ min: 0 }}
                      />
                      <TextField
                        label="Modalidad"
                        name="modality"
                        value={form.modality}
                        onChange={handleChange}
                        placeholder="Presencial, online, a domicilio..."
                        fullWidth
                      />
                    </Stack>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Box>
                        <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>
                          Requiere reserva o turno
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Publica la reserva online y, al guardar, te lleva a definir los horarios.
                        </Typography>
                      </Box>
                      <Switch
                        name="requiresBooking"
                        checked={isReservableService(form)}
                        onChange={(event) => {
                          const enabled = event.target.checked;
                          setForm((current) => ({
                            ...current,
                            reservable: enabled,
                            requiresBooking: enabled,
                            bookingProvider: enabled
                              ? (current.bookingProvider === "calendly" ? "calendly" : "webprofit")
                              : "none",
                            ...(!enabled
                              ? {
                                  calendlyUrl: "",
                                  calendlyDisplayMode: "popup",
                                  bookingConfirmationMode: "manual",
                                  bookingPaymentMode: "none",
                                  bookingDepositType: "fixed",
                                  bookingDepositValue: "0",
                                  capacity: "1",
                                  bufferBeforeMinutes: "0",
                                  bufferAfterMinutes: "0",
                                  meetingUrl: "",
                                  bookingInstructions: "",
                                  cancellationWindowHours: "0",
                                }
                              : {}),
                          }));
                        }}
                      />
                    </Stack>

                    {isReservableService(form) ? (
                      <>
                        <Divider />
                        <TextField
                          select
                          label="Agenda para la reserva"
                          name="bookingProvider"
                          value={form.bookingProvider === "calendly" ? "calendly" : "webprofit"}
                          onChange={handleChange}
                          helperText="Podés usar la agenda de WebProfit o delegar la elección del horario a Calendly."
                          fullWidth
                        >
                          <MenuItem value="webprofit">Agenda de WebProfit</MenuItem>
                          <MenuItem value="calendly">Calendly</MenuItem>
                        </TextField>

                        {form.bookingProvider === "calendly" ? (
                          <Stack spacing={2}>
                            <TextField
                              label="Enlace de Calendly"
                              name="calendlyUrl"
                              value={form.calendlyUrl}
                              onChange={handleChange}
                              placeholder="https://calendly.com/tu-negocio/turno"
                              error={Boolean(fieldErrors.calendlyUrl)}
                              helperText={fieldErrors.calendlyUrl || "Se abrirá este evento para que el cliente elija el horario."}
                              fullWidth
                            />
                            <TextField
                              select
                              label="Cómo mostrar Calendly"
                              name="calendlyDisplayMode"
                              value={form.calendlyDisplayMode}
                              onChange={handleChange}
                              fullWidth
                            >
                              <MenuItem value="popup">Ventana emergente</MenuItem>
                              <MenuItem value="inline">Integrado en la página</MenuItem>
                            </TextField>
                            <TextField
                              label="Instrucciones para reservar"
                              name="bookingInstructions"
                              value={form.bookingInstructions}
                              onChange={handleChange}
                              multiline
                              minRows={2}
                              placeholder="Indicaciones adicionales para el cliente..."
                              fullWidth
                            />
                          </Stack>
                        ) : (
                          <>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                          <TextField
                            select
                            label="Confirmación del turno"
                            name="bookingConfirmationMode"
                            value={form.bookingConfirmationMode}
                            onChange={handleChange}
                            fullWidth
                          >
                            <MenuItem value="manual">Confirmación manual</MenuItem>
                            <MenuItem value="automatic">Confirmación automática</MenuItem>
                          </TextField>
                          <TextField
                            select
                            label="Pago para reservar"
                            name="bookingPaymentMode"
                            value={form.bookingPaymentMode}
                            onChange={handleChange}
                            fullWidth
                          >
                            <MenuItem value="none">No requiere pago</MenuItem>
                            <MenuItem value="full">Pago total</MenuItem>
                            <MenuItem value="deposit">Seña</MenuItem>
                          </TextField>
                          <TextField
                            label="Cupos por horario"
                            name="capacity"
                            type="number"
                            value={form.capacity}
                            onChange={handleChange}
                            inputProps={{ min: 1, max: 10000 }}
                            error={Boolean(fieldErrors.capacity)}
                            helperText={fieldErrors.capacity || ""}
                            fullWidth
                          />
                        </Stack>

                        {form.bookingPaymentMode === "deposit" ? (
                          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                            <TextField
                              select
                              label="Tipo de seña"
                              name="bookingDepositType"
                              value={form.bookingDepositType}
                              onChange={handleChange}
                              fullWidth
                            >
                              <MenuItem value="fixed">Monto fijo</MenuItem>
                              <MenuItem value="percentage">Porcentaje</MenuItem>
                            </TextField>
                            <TextField
                              label={
                                form.bookingDepositType === "percentage"
                                  ? "Porcentaje de seña"
                                  : "Monto de seña"
                              }
                              name="bookingDepositValue"
                              type="number"
                              value={form.bookingDepositValue}
                              onChange={handleChange}
                              inputProps={{
                                min: 0,
                                max:
                                  form.bookingDepositType === "percentage"
                                    ? 100
                                    : undefined,
                              }}
                              fullWidth
                            />
                          </Stack>
                        ) : null}

                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                          <TextField
                            label="Margen antes (minutos)"
                            name="bufferBeforeMinutes"
                            type="number"
                            value={form.bufferBeforeMinutes}
                            onChange={handleChange}
                            inputProps={{ min: 0 }}
                            fullWidth
                          />
                          <TextField
                            label="Margen después (minutos)"
                            name="bufferAfterMinutes"
                            type="number"
                            value={form.bufferAfterMinutes}
                            onChange={handleChange}
                            inputProps={{ min: 0 }}
                            fullWidth
                          />
                          <TextField
                            label="Cancelación mínima (horas)"
                            name="cancellationWindowHours"
                            type="number"
                            value={form.cancellationWindowHours}
                            onChange={handleChange}
                            inputProps={{ min: 0 }}
                            fullWidth
                          />
                        </Stack>

                        <TextField
                          label="Lugar o dirección"
                          name="serviceLocation"
                          value={form.serviceLocation}
                          onChange={handleChange}
                          placeholder="Consultorio, local, domicilio del cliente..."
                          fullWidth
                        />
                        <TextField
                          label="Enlace de videollamada"
                          name="meetingUrl"
                          value={form.meetingUrl}
                          onChange={handleChange}
                          placeholder="https://meet.google.com/..."
                          fullWidth
                        />
                        <TextField
                          label="Instrucciones para reservar"
                          name="bookingInstructions"
                          value={form.bookingInstructions}
                          onChange={handleChange}
                          multiline
                          minRows={2}
                          placeholder="Qué debe traer, cómo prepararse, condiciones del turno..."
                          fullWidth
                        />
                          </>
                        )}
                      </>
                    ) : null}
                  </Stack>
                </Paper>
              ) : null}

                  {supportsVariantsForKind(form.erpItemKind) ? (
                    <ProductVariantsEditor
                      form={form}
                      setForm={setForm}
                      imageOptions={variantImageOptions(existingImages, previewUrls)}
                      compact={compactLayout}
                    />
                  ) : null}
                </>
              ) : null}

              <Divider />
              <ItemFormHeading
                step={form.erpItemKind === "service" || supportsVariantsForKind(form.erpItemKind) ? "3" : "2"}
                title="Uso operativo y costos"
                description="Definí cómo participa el ítem en la operación. El inventario no se edita desde este formulario: las existencias cambian únicamente mediante movimientos."
              />

              <ItemOperationalFields
                form={form}
                setForm={setForm}
                canManageCosts={canManageCosts}
                canManageAccounting={canManageAccounting}
                membershipsEnabled={membershipsEnabled}
                compact={compactLayout}
              />

              <Divider />
              <ItemFormHeading
                step={form.erpItemKind === "service" || supportsVariantsForKind(form.erpItemKind) ? "4" : "3"}
                title="Detalles para publicar"
                description="Terminá con etiquetas, datos propios del rubro e imágenes. Nada de esto altera el control operativo."
              />

              <TextField
                label="Etiquetas"
                name="tags"
                value={form.tags}
                onChange={handleChange}
                placeholder="Ej.: premium, instalación, exterior"
                helperText="Separalas con comas. Sirven para ordenar y describir cualquier rubro."
                fullWidth
              />

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                <Stack spacing={1.5}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ sm: "center" }}
                    spacing={1}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>
                        Datos adicionales
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Agregá campos propios del rubro: marca, material,
                        cobertura, zona, nivel, garantía, etc.
                      </Typography>
                    </Box>
                    <Button
                      type="button"
                      startIcon={<AddCircleOutlineRoundedIcon />}
                      onClick={addAttribute}
                      sx={{ textTransform: "none", fontWeight: 850 }}
                    >
                      Agregar dato
                    </Button>
                  </Stack>

                  {form.attributes.length ? <Divider /> : null}
                  {form.attributes.map((row, index) => (
                    <Stack
                      key={`attribute-${index}`}
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ sm: "center" }}
                    >
                      <TextField
                        label="Campo"
                        value={row.label}
                        onChange={(event) =>
                          handleAttributeChange(
                            index,
                            "label",
                            event.target.value,
                          )
                        }
                        placeholder="Ej.: Cobertura"
                        fullWidth
                      />
                      <TextField
                        label="Valor"
                        value={row.value}
                        onChange={(event) =>
                          handleAttributeChange(
                            index,
                            "value",
                            event.target.value,
                          )
                        }
                        placeholder="Ej.: Todo San Rafael"
                        fullWidth
                      />
                      <IconButton
                        type="button"
                        onClick={() => removeAttribute(index)}
                        aria-label="Eliminar dato"
                        sx={{ color: BRAND.orange }}
                      >
                        <DeleteRoundedIcon />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
              </Paper>

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>
                    Ítem activo
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}
                  >
                    Si está inactivo no podrá utilizarse en nuevas operaciones. La visibilidad pública se configura por separado.
                  </Typography>
                </Box>
                <Switch
                  name="active"
                  checked={form.active}
                  onChange={handleChange}
                />
              </Stack>

              <Box>
                <Typography sx={{ fontWeight: 900, color: BRAND.dark, mb: 1 }}>
                  Imágenes
                </Typography>

                {existingImages.length > 0 ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    sx={{ mb: 1.5 }}
                  >
                    {existingImages.map((img) => (
                      <Box
                        key={img.identifier}
                        sx={{ position: "relative", mb: 1 }}
                      >
                        <Box
                          component="img"
                          src={img.url}
                          alt="Imagen actual"
                          sx={{
                            width: 76,
                            height: 76,
                            objectFit: "cover",
                            borderRadius: 2,
                            border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),0.1)",
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleRemoveExistingImage(img.identifier)
                          }
                          aria-label="Quitar imagen actual"
                          sx={REMOVE_IMAGE_BUTTON_SX}
                        >
                          <CloseRoundedIcon sx={{ position: "absolute", top: 5, right: 5, fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Stack>
                ) : null}

                {previewUrls.length > 0 ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    sx={{ mb: 1.5 }}
                  >
                    {previewUrls.map((url, index) => (
                      <Box key={url} sx={{ position: "relative", mb: 1 }}>
                        <Box
                          component="img"
                          src={url}
                          alt={`Nueva imagen ${index + 1}`}
                          sx={{
                            width: 76,
                            height: 76,
                            objectFit: "cover",
                            borderRadius: 2,
                            border: `1px solid ${BRAND.orange}`,
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            maxWidth: 76,
                            textAlign: "center",
                          }}
                        >
                          {formatImageSize(selectedFiles[index]?.size)}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveSelectedFile(index)}
                          aria-label={`Quitar nueva imagen ${index + 1}`}
                          sx={REMOVE_IMAGE_BUTTON_SX}
                        >
                          <CloseRoundedIcon sx={{ position: "absolute", top: 5, right: 5, fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Stack>
                ) : null}

                {processingImages ? (
                  <Box sx={{ mb: 1.5 }}>
                    <LinearProgress
                      variant={Number.isFinite(imagePreparationProgress) ? "determinate" : "indeterminate"}
                      value={Number.isFinite(imagePreparationProgress) ? imagePreparationProgress : undefined}
                      aria-label="Progreso de preparación de imágenes"
                    />
                    <Typography variant="caption" color="text.secondary">
                      Optimizando imágenes antes de subir
                      {Number.isFinite(imagePreparationProgress) ? `… ${imagePreparationProgress}%` : "…"}
                    </Typography>
                  </Box>
                ) : null}

                {saving && selectedFiles.length ? (
                  <Box sx={{ mb: 1.5 }}>
                    <LinearProgress
                      variant={Number.isFinite(imageUploadProgress) ? "determinate" : "indeterminate"}
                      value={Number.isFinite(imageUploadProgress) ? imageUploadProgress : undefined}
                      aria-label="Progreso de carga de imágenes"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {imageUploadProgress === 100
                        ? "Imágenes enviadas. El servidor está terminando de procesarlas…"
                        : `Subiendo imágenes${Number.isFinite(imageUploadProgress) ? `… ${imageUploadProgress}%` : "…"}`}
                    </Typography>
                  </Box>
                ) : null}

                {imageError ? (
                  <Alert severity="warning" sx={{ mb: 1.5 }}>
                    {imageError}
                  </Alert>
                ) : null}

                <Button
                  component="label"
                  disabled={
                    processingImages ||
                    existingImages.length + selectedFiles.length >= MAX_IMAGES
                  }
                  startIcon={<UploadRoundedIcon />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 850,
                    color: BRAND.dark,
                    border: "1px dashed rgba(var(--wp-panel-ink-rgb, 25,36,47),0.3)",
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                  }}
                >
                  {existingImages.length || selectedFiles.length
                    ? "Agregar más imágenes"
                    : "Subir imágenes"}
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleFilesChange}
                  />
                </Button>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.7,
                    color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)",
                  }}
                >
                  {existingImages.length + selectedFiles.length}/{MAX_IMAGES} imágenes · JPG, PNG o WebP
                  {!mobile ? ` · hasta ${formatImageSize(PRODUCT_IMAGE_MAX_SOURCE_BYTES)} cada una. El panel las optimiza antes de enviarlas.` : ""}
                </Typography>
              </Box>
                </Stack>
              </Collapse>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions
            sx={{
              flex: "0 0 auto",
              p: mobile ? 1.2 : 2,
              gap: 1,
              bgcolor: "var(--wp-panel-surface, #fff)",
              borderTop: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.10)",
              boxShadow: "0 -10px 28px rgba(var(--wp-panel-shadow-rgb, 25,36,47),.06)",
            }}
          >
            <Button
              onClick={handleClose}
              disabled={saving || processingImages}
              fullWidth={mobile}
              sx={{ textTransform: "none", fontWeight: 850 }}
            >
              Cancelar
            </Button>
            {guidedGastronomicForm && gastroStep > 0 ? (
              <Button
                type="button"
                onClick={() => setGastroStep((current) => Math.max(0, current - 1))}
                disabled={saving || processingImages}
                fullWidth={mobile}
                sx={{ textTransform: "none", fontWeight: 850 }}
              >
                Anterior
              </Button>
            ) : null}
            <Button
              type={createdSupplyId ? "button" : guidedGastronomicForm && gastroStep < gastronomicLastStep ? "button" : "submit"}
              onClick={createdSupplyId
                ? handleClose
                : guidedGastronomicForm && gastroStep < gastronomicLastStep
                  ? () => {
                    setFormFeedback(null);
                    setGastroStep((current) => Math.min(gastronomicLastStep, current + 1));
                    formContentRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
                  }
                : undefined}
              disabled={saving || processingImages}
              variant="contained"
              startIcon={saving || processingImages ? <WebProfitSpinner size={17} /> : undefined}
              fullWidth={mobile}
              sx={{
                bgcolor: BRAND.orange,
                borderRadius: 999,
                minHeight: 44,
                textTransform: "none",
                fontWeight: 950,
                "&:hover": { bgcolor: "#D9472B" },
              }}
            >
              {createdSupplyId
                ? "Listo"
                : guidedGastronomicForm && gastroStep < gastronomicLastStep
                  ? "Continuar"
                  : saving && savingStage === "promotions"
                    ? "Guardando promociones…"
                    : saving && selectedFiles.length
                  ? imageUploadProgress === 100
                    ? "Procesando imágenes…"
                    : `Subiendo${Number.isFinite(imageUploadProgress) ? ` ${imageUploadProgress}%` : "…"}`
                  : saving
                    ? "Guardando..."
                    : editingProduct
                      ? "Guardar cambios"
                      : form.erpItemKind === "supply"
                        ? "Crear insumo"
                        : guidedGastronomicForm
                          ? "Crear producto"
                          : "Crear ítem"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 4, overflow: "hidden" } }}
      >
        <Box
          sx={{
            p: { xs: 2.5, sm: 3 },
            color: "#fff",
            background: "linear-gradient(135deg,#19242F 0%,#30485B 100%)",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box
              sx={{
                width: 52,
                height: 52,
                flex: "0 0 auto",
                borderRadius: 3,
                display: "grid",
                placeItems: "center",
                bgcolor: "rgba(243,90,58,.18)",
                color: "#FF9A82",
              }}
            >
              <LockClockRoundedIcon />
            </Box>
            <Box>
              <Typography variant="overline" sx={{ color: "rgba(255,255,255,.62)", fontWeight: 950, letterSpacing: ".1em" }}>
                LÍMITE DEL TRIAL
              </Typography>
              <DialogTitle sx={{ p: 0, fontWeight: 950, fontSize: { xs: 24, sm: 30 }, lineHeight: 1.12 }}>
                La prueba incluye hasta {trialItemsLimit} publicaciones
              </DialogTitle>
            </Box>
          </Stack>
        </Box>

        <DialogContent sx={{ pt: 2.7 }}>
          <Typography sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", lineHeight: 1.65 }}>
            Ya usaste las {trialItemsLimit} publicaciones incluidas en la prueba. Podés editar o borrar las existentes; para cargar una nueva, liberá un lugar o activá el plan del proyecto.
          </Typography>

          <Stack spacing={1.1} sx={{ mt: 2 }}>
            {[`Editar las ${trialItemsLimit} publicaciones existentes`, "Cargar productos y servicios adicionales", "Mantener las funciones comerciales habilitadas"].map((label, index) => (
              <Stack key={label} direction="row" spacing={1.1} alignItems="center">
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    bgcolor: index === 0 ? "rgba(46,125,50,.09)" : "rgba(243,90,58,.09)",
                    color: index === 0 ? "#2E7D32" : BRAND.orange,
                    fontWeight: 950,
                    fontSize: 13,
                  }}
                >
                  {index === 0 ? "✓" : index + 1}
                </Box>
                <Typography sx={{ fontWeight: 850, color: BRAND.dark }}>{label}</Typography>
              </Stack>
            ))}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, pt: 1, flexDirection: { xs: "column-reverse", sm: "row" }, gap: 1 }}>
          <Button
            fullWidth
            onClick={() => {
              setUpgradeOpen(false);
              navigate(landingPath);
            }}
            sx={{ textTransform: "none", fontWeight: 900, borderRadius: 999 }}
          >
            Completar mi landing
          </Button>
          <Button
            fullWidth
            variant="contained"
            startIcon={<CreditCardRoundedIcon />}
            onClick={() => {
              setUpgradeOpen(false);
              navigate(billingPath);
            }}
            sx={{
              bgcolor: BRAND.orange,
              textTransform: "none",
              fontWeight: 950,
              borderRadius: 999,
              "&:hover": { bgcolor: "#DB4B2F" },
            }}
          >
            Ver planes y activar
          </Button>
        </DialogActions>
      </Dialog>

      <GastronomicMenuOrderDialog
        open={menuOrderOpen}
        onClose={() => { if (!menuOrderSaving) setMenuOrderOpen(false); }}
        products={products}
        canEdit={canUpdateItems}
        saving={menuOrderSaving}
        error={menuOrderError}
        onSave={handleSaveMenuOrder}
      />

      <BarcodeScannerDialog
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={handleScannedItemCode}
        title="Buscar ítem con lectora"
        helper="Escaneá un código interno, SKU o código de barras de variante. WebProfit buscará una coincidencia exacta."
      />
      <BarcodeScannerDialog
        open={createScannerOpen}
        onClose={() => setCreateScannerOpen(false)}
        onScan={handleScannedCreateCode}
        title="Usar etiqueta existente"
        helper="Escaneá el código que ya trae este artículo. WebProfit verificará que no esté asignado y lo usará como código del nuevo ítem. Esto no modifica stock."
        submitLabel="Usar código"
      />
    </Stack>
  );
}
